import React, { useState, useEffect, useRef } from 'react';
import UserFilter from './UserFilter';
import CollectionDetail from './CollectionDetail';
import defaultAvatar from '../../assets/images/project_placeholder@2x.jpg';
import Image from '../Image';
import userService from '../services/UserService';
import Button from '../submission_boards/component/Button';
import { CircleOffIcon, RefreshCcwIcon, ArrowDownNarrowWide } from 'lucide-react';
import { getStorageNotification, saveStorageNotification } from './storageUtils';
import FilePreviewer from '../submission_kits/components/FilePreviewer';
import Breadcrumb from '../shared/Breadcrumb';
import { ProfileDetailView } from '../profiles/ProfileViews';
import userSkillsService, { UserSkill } from '../services/UserSkillsService';
import { useIsBreakPoint } from '../submission_boards/component/useIsMobile';
import usePageTracking from '../shared/UsePageTracking';

export interface User {
  id: number;
  uuid: string;
  firstName: string;
  lastName: string;
  country: string;
  ethnicities: string[];
  gender: string;
  height: number;
  weight: number;
  avatarUrl: string;
  croppedAvatarUrl: string;
  measure_system: 'imperial' | 'metric';
  is_favorited?: boolean;
  subscription_status?: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'incomplete';
  subscription_tier?: 'free' | 'pro' | 'premium';
  role?: string;
  super_admin?: boolean;
}

interface CurrentUser extends User {
  super_admin: boolean;
}

export interface Group {
  id: number;
  title: string;
  status?: 'Active' | 'Archived';
  description?: string;
  hashtag?: string;
  teamMembers: number;
  members: string[];
  teamMemberAvatars: string[];
  archived?: boolean;
  shared?: boolean;
  hasAccess?: boolean;
  shared_users?: User[];
  isOwner?: boolean;
}


interface DirectoryProps {
  users: User[];
  currentUser: CurrentUser;
  measurementRanges: {
    height: {
      min: number;
      max: number;
    };
    weight: {
      min: number;
      max: number;
    };
  };
}

interface GroupMenuProps {
  user: User;
  groups: Group[];
  onAddToGroup: (groupId: number) => void;
  onOpenCreateModal: () => void;
  onClose: () => void;
  isCreating: boolean;
}

function GroupContextMenu({ user, groups, onAddToGroup, onOpenCreateModal, onClose, isCreating }: GroupMenuProps) {
  const [selectedGroupIds, setSelectedGroupIds] = React.useState<number[]>([]);

  const activeGroups = groups.filter(g => (g.status === 'Active' || !g.status) && !g.archived && g.members.indexOf(user.uuid) < 0);
  // console.log('activeGroups', activeGroups);
  React.useEffect(() => {
    setSelectedGroupIds([]);
  }, [user?.uuid, groups]);

  const toggleSelectGroup = (groupId: number) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const handleDone = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (selectedGroupIds.length === 0) {
      onClose();
      return;
    }

    try {
      await Promise.all(selectedGroupIds.map((gid) => onAddToGroup(gid)));
    } catch (err) {
      console.error('Error adding to groups:', err);
    } finally {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-customGray z-50 flex items-center justify-center p-2 sm:p-4"
      // style={{ backgroundColor: '#6b7a8a' }}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      aria-modal="true"
      role="dialog"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full h-full sm:h-auto max-w-3xl mx-auto overflow-hidden flex flex-col"
        style={{ maxHeight: '95vh' }}
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src={user?.croppedAvatarUrl || defaultAvatar}
                alt={`${user?.firstName || ''} ${user?.lastName || ''}`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base sm:text-lg font-semibold text-gray-900">
                Add <span className="font-bold">{user?.firstName} {user?.lastName}</span> to a Collection:
              </div>
              <div className="text-xs sm:text-sm text-gray-500 mt-1">
                Select one or more collections where this member should be added.
              </div>
            </div>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 flex-shrink-0 ml-2"
            aria-label="Close"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none">
              <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {/* Body: grid of collection cards */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 sm:py-6">
          {/* Responsive grid - 1 column mobile, 2 on tablet, 3 on desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {activeGroups.length === 0 && (
              <div className="col-span-full py-8 sm:py-12 text-center text-gray-500">
                No active collections yet.
              </div>
            )}

            {activeGroups.map((group) => {
              const selected = selectedGroupIds.includes(group.id);
              const groupAvatars = group.teamMemberAvatars || [];

              const main = groupAvatars[0] || defaultAvatar;
              const rightTop = groupAvatars[1] || defaultAvatar;
              const rightBottom = groupAvatars[2] || defaultAvatar;

              return (
                <div
                  key={group.id}
                  className={`rounded-xl bg-white border-2 ${selected ? 'border-blue-500 shadow-lg' : 'border-gray-200'
                    } flex flex-col cursor-pointer transition-all hover:shadow-md`}
                  style={{
                    padding: '12px',
                    minHeight: '220px'
                  }}
                  onClick={() => toggleSelectGroup(group.id)}
                  role="button"
                  aria-pressed={selected}
                >
                  {/* Images area */}
                  <div className="flex gap-2 mb-3" style={{ height: '150px' }}>
                    {/* Left large image - takes up 50% */}
                    <div className="w-1/2 h-full rounded-lg overflow-hidden bg-gray-100">
                      {main ? (
                        <img
                          src={main}
                          onError={(e) => {
                            e.currentTarget.src = defaultAvatar;
                          }}
                          alt={group.title}
                          className="w-full h-full object-contain"
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Right column with two stacked images - takes up 50% */}
                    <div className="flex flex-col gap-2 w-1/2">
                      <div className="flex-1 rounded-lg overflow-hidden bg-gray-100">
                        {rightTop ? (
                          <img
                            src={rightTop}
                            onError={(e) => {
                              e.currentTarget.src = defaultAvatar;
                            }}
                            alt={`${group.title}-1`}
                            className="w-full h-full object-contain"
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 rounded-lg overflow-hidden bg-gray-100">
                        {rightBottom ? (
                          <img
                            src={rightBottom}
                            onError={(e) => {
                              e.currentTarget.src = defaultAvatar;
                            }}
                            alt={`${group.title}-2`}
                            className="w-full h-full object-contain"
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Title and checkbox */}
                  <div className="flex items-center justify-between mt-auto">
                    <div className="text-sm sm:text-base font-semibold text-gray-900 truncate flex-1 mr-2">
                      {group.title}
                    </div>
                    <div
                      className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'
                        }`}
                    >
                      {selected && (
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" viewBox="0 0 24 24" fill="none">
                          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer actions */}
        <div className="border-t border-gray-200 p-4 sm:p-6 flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onOpenCreateModal(); onClose(); }}
            disabled={isCreating}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 rounded-lg bg-white text-sm sm:text-base font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none">
              <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Create a New Collection
          </button>

          <button
            onClick={handleDone}
            className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg bg-blue-600 text-white text-sm sm:text-base font-semibold hover:bg-blue-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

const areFilterParamsEqual = (a: any, b: any) => {
  if (a === b) return true;
  if (!a || !b) return true; // Treat null/undefined/empty as equal to prevent resetting on initial mount

  const keysA = Object.keys(a).filter((k) => k !== 'filterPresent');
  const keysB = Object.keys(b).filter((k) => k !== 'filterPresent');

  // If both have no filter keys, they are equal
  if (keysA.length === 0 && keysB.length === 0) return true;

  // If one has keys and the other doesn't, they are different
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    const valA = a[key];
    const valB = b[key];

    if (Array.isArray(valA) && Array.isArray(valB)) {
      if (valA.length !== valB.length) return false;
      // Sort to make array comparison order-independent
      const sortedA = [...valA].sort();
      const sortedB = [...valB].sort();
      if (!sortedA.every((v, i) => v === sortedB[i])) return false;
    } else if (valA !== valB) {
      return false;
    }
  }

  return true;
};

export default function Directory({ users, currentUser, measurementRanges }: DirectoryProps) {
  const [filteredUsers, setFilteredUsers] = useState<User[]>(users);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  // const [favoritesUsers, setFavoritesUsers] = useState<Set<string>>(new Set());
  const [favoritesUsers, setFavoritesUsers] = useState([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [showGroupMenu, setShowGroupMenu] = useState<string | null>(null);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [selectedUserForGroup, setSelectedUserForGroup] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'favorites' | 'collections'>('all');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFavoritesSortMenu, setShowFavoritesSortMenu] = useState(false);
  const [sortOption, setSortOption] = useState<'favorites' | 'newest' | 'newest-pro' | 'oldest' | 'name-asc' | 'name-desc'>('newest-pro');
  const [showCollectionFilter, setShowCollectionFilter] = useState(false);
  const [collectionFilter, setCollectionFilter] = useState<'all' | 'active' | 'archived'>('active');
  const [openModals, setOpenModals] = useState<Record<string, { group: Group; position: { top: number; left: number } }>>({});
  const cardRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isMobileFiltersOpenFav, setIsMobileFiltersOpenFav] = useState(false);

  // const [filteredUsersForFavorites, setFilteredUsersForFavorites] = useState<User[]>([]);
  const [isLoadingFilteredUsers, setIsLoadingFilteredUsers] = useState(false);
  // member_uuid: [group_ids]
  const [userCollections, setUserCollections] = useState<Record<string, number[]>>({});
  const [viewingCollection, setViewingCollection] = useState<Group | null>(null);
  const [loadingCollection, setLoadingCollection] = useState(false);
  const [showSharedCollection, setshowSharedCollection] = useState(false);

  const [showAddFavoritesModal, setShowAddFavoritesModal] = useState(false);
  const [addFavoritesSearchTerm, setAddFavoritesSearchTerm] = useState('');
  const [selectedUsersToFavorite, setSelectedUsersToFavorite] = useState<string[]>([]);
  const [notification, setNotification] = useState<{ message: string | React.ReactNode; type: 'success' | 'error' | 'info' } | null>(null);

  const [manageMembersGroup, setManageMembersGroup] = useState<Group | null>(null);
  const [manageMembersActiveTab, setManageMembersActiveTab] = useState<'add' | 'current'>('add');
  const [searchMember, setSearchMember] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [currentMembers, setCurrentMembers] = useState<User[]>([]);
  const [availableUsersForManage, setAvailableUsersForManage] = useState<User[]>([]);
  const [editingDescriptionId, setEditingDescriptionId] = useState<number | null>(null);
  const [descriptionValues, setDescriptionValues] = useState<Record<number, string>>({});
  const [editingTitleId, seteditingTitleId] = useState(null);

  const [shareGroup, setShareGroup] = useState<Group | null>(null);
  const [shareSearchTerm, setShareSearchTerm] = useState('');
  const [selectedShareUsers, setSelectedShareUsers] = useState<string[]>([]);
  const [availableShareUsers, setAvailableShareUsers] = useState<User[]>([]);
  const [currentlySharedWith, setCurrentlySharedWith] = useState<User[]>([]);
  const [favoritesSortOption, setFavoritesSortOption] = useState<'newest' | 'oldest' | 'name-asc' | 'name-desc'>('newest');
  const [addFavoritesFilters, setAddFavoritesFilters] = useState({
    gender: '',
    ethnicity: '',
    country: '',
    weightMin: measurementRanges.weight.min,
    weightMax: measurementRanges.weight.max,
    heightMin: measurementRanges.height.min,
    heightMax: measurementRanges.height.max,
  });
  const { isMobile, isLg1100 } = useIsBreakPoint();

  const [profileModalUser, setprofileModalUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [breadCrumps, setbreadCrumps] = useState([
    { name: 'Member Directory', href: '', current: true },
  ])

  const [masterSkills, setMasterSkills] = useState<UserSkill[]>([]);


  const openFavoritesMembers = () => {
    window.history.pushState({}, '', `/users/directory?tab=favorites`);
    setActiveTab('favorites');
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToCollections = () => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const [filteredParams, setfilteredParams] = useState({});
  const [isUserLoading, setIsUserLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(users && users.length > 0 ? 2 : 1);
  const isInitialMount = useRef(true);
  const prevFilteredParamsRef = useRef(filteredParams);
  const prevSortOptionRef = useRef(sortOption);
  const [showAddFavSortMenu, setShowAddFavSortMenu] = useState(false);
  const observerRef = useRef(null);
  const observerAddFavRef = useRef(null);

  // const listRef = useRef(null);
  const [noMoreData, setNoMoreData] = useState<string>('');
  const perPage: number = 12; // Number of records to fetch per page



  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    project_type: '',
    status: 'Active' as const,
    start_date: '',
    due_date: '',
    hashtag: '',
    member_ids: [] as string[]
  });

  // const loadAllUserCollections = async (userUuids: string[]) => {
  //   if (userUuids.length === 0) return;

  //   try {
  //     const response = await fetch('/api/v1/users/members_group', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Accept': 'application/json',
  //         'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  //       },
  //       credentials: 'same-origin',
  //       body: JSON.stringify({ user_uuids: userUuids })
  //     });

  //     if (response.ok) {
  //       const data = await response.json();
  //       setUserCollections(data.user_collections || {});
  //     }
  //   } catch (err) {
  //     console.error('Error fetching user collections:', err);
  //   }
  // };

  // const loadGroupMembers = async () => {
  //   try {
  //     const response = await fetch('/api/v1/users/members_group', {
  //       method: 'GET',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Accept': 'application/json',
  //         'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  //       },
  //       credentials: 'same-origin',
  //       // body: JSON.stringify({ user_uuids: [] })
  //     });

  //     if (response.ok) {
  //       const data = await response.json();
  //       setUserCollections(data.user_collections || {});
  //     }
  //   } catch (err) {
  //     console.error('Error fetching user collections:', err);
  //   }
  // };

  const showNotification = (message: string | React.ReactNode, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const isSuperAdmin = currentUser.super_admin;

  let pageTitle = 'Member Directory';
  if (activeTab === 'favorites') pageTitle = 'Favorites';
  if (activeTab === 'collections') pageTitle = 'Collections';

  usePageTracking(pageTitle);
  useEffect(() => {

    switch (activeTab) {
      case 'all':
        setbreadCrumps(
          [
            { name: 'Member Directory', href: '', current: true }
          ]
        )
        break;
      case 'favorites':
        setbreadCrumps(
          [
            { name: 'Member Directory', href: '/users/directory?tab=all', current: false },
            { name: 'Favorites', href: '#', current: true },
          ]
        )
        break;
      case 'collections':
        setbreadCrumps(
          [
            { name: 'Member Directory', href: '/users/directory?tab=all', current: false },
            { name: 'Collections', href: '#', current: true },
          ]
        )
        break;

      default:
        break;
    }

  }, [activeTab])
  useEffect(() => {
    handleLocationParam(true);
    // const urlParams = new URLSearchParams(window.location.search);
    // console.log('urlParams', urlParams);
    // const collectionId = urlParams.get('collection');
    // const tab = urlParams.get('tab');

    // if (collectionId && !isNaN(parseInt(collectionId))) {
    //   setshowSharedCollection(true);
    //   loadAndOpenCollection(parseInt(collectionId));
    // } else {
    //   loadFavorites();
    //   loadGroups();
    //   const hasVisited = localStorage.getItem('member_directory_visited');
    //   if (!hasVisited) {
    //     setShowGuideCard(true);
    //     saveStorageNotification('member_directory', 1);
    //     localStorage.setItem('member_directory_visited', 'true');
    //   }
    // }
  }, []);

  useEffect(() => {
    const handlePopState = (event) => {
      console.log('User navigated using browser history (Back or Forward)!');
      console.log('Current:', event);
      console.log('Current state:', event.state);
      handleLocationParam(false);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const loadUsers = async (pageParam) => {
    try {
      let combinedParams: any = { ...filteredParams };



      let _users = await userService.getUsers(pageParam, perPage, combinedParams, sortOption);
      if (_users && _users.length > 0) {
        // setPage((prev) => prev + 1);
        const nextPage = page + 1;
        setPage(nextPage);
        setFilteredUsers((prevRecords) => [...prevRecords, ..._users]);
        if (_users.length < perPage) {
          // This was the last page - we got fewer profiles than requested
          setNoMoreData('All Profiles Loaded');
        } else {
          setNoMoreData('');
        }
        return;
      }
      if (pageParam > 1) {
        setNoMoreData('All Profiles Loaded');
      } else if (filteredUsers.length <= 0) {
        setNoMoreData('No matching profiles.');
      } else {
        setNoMoreData('All Profiles Loaded');
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      console.log('loadUsers: setIsUserLoading false: ', isUserLoading);
      setIsUserLoading(false);
    }
  };

  const loadMore = async () => {
    if (activeTab === 'favorites' && !showAddFavoritesModal) {
      setCurrentPage((prev) => prev + 1);
      return;
    }
    if (isUserLoading) {
      console.log('sb: directory isLoading');
      return;
    }
    console.log('loadUsers: setIsUserLoading true: ', isUserLoading);
    setIsUserLoading(true);
  };

  useEffect(() => {
    const isFiltersEqual = areFilterParamsEqual(filteredParams, prevFilteredParamsRef.current);
    const isSortEqual = sortOption === prevSortOptionRef.current;

    prevFilteredParamsRef.current = filteredParams;
    prevSortOptionRef.current = sortOption;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (isFiltersEqual && isSortEqual) {
      return; // Do nothing if the filter/sort values didn't actually change
    }

    if (!isUserLoading) {
      setNoMoreData('');
      setPage(1);
      setFilteredUsers([]);
      console.log('loadMore:useEffect filters/sort change');
      loadMore();
    }
  }, [filteredParams, sortOption]);

  // useEffect(() => {
  //   if (filteredUsers.length > 0) {
  //     const favoritedUserUuids = filteredUsers
  //       .filter(user => favoritesUsers.findIndex((item) => item.uuid == user.uuid) >= 0) // favoritesUsers.has(user.uuid))
  //       .map(user => user.uuid);

  //     if (favoritedUserUuids.length > 0) {
  //       loadAllUserCollections(favoritedUserUuids);
  //     }
  //   }
  // }, [filteredUsers, favoritesUsers]);

  useEffect(() => {
    if (isUserLoading) {
  //     console.log('Directory::useEffect isUserLoading');
      const load = async () => {
        await loadUsers(page);
      };
      load();
    }
  }, [isUserLoading]);


  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        console.log('Directory::useEffect observerRef.current, filteredUsers, activeTab loadMore');
        loadMore();
      }
    }, {
      rootMargin: '0px 0px 100px 0px',
      threshold: 0
    });

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }
    return () => {
      observer.disconnect()
    };
  }, [observerRef.current, filteredUsers, activeTab]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        console.log('Directory::useEffect observerAddFavRef loadMore');
        loadMore();
      }
    }, {
      rootMargin: '0px 0px 50px 0px',
      threshold: 0
    });

    if (observerAddFavRef.current) {
      observer.observe(observerAddFavRef.current);
    }
    return () => {
      observer.disconnect()
    };
  }, [observerAddFavRef.current]);

  useEffect(() => {
    // console.log('favoritesUsers', favoritesUsers);
    // const loadFilteredUsers = async () => {
    //   const filtered = await getFilteredUsersForAddFavorites();
    //   setFilteredUsersForFavorites(filtered);
    // };

    if (showAddFavoritesModal) {
      // loadFilteredUsers();
    }
  }, [showAddFavoritesModal, addFavoritesSearchTerm, addFavoritesFilters, favoritesUsers]);

  useEffect(() => {
    searchShareUsers(shareSearchTerm);
  }, [shareSearchTerm]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isModalOpen]);

  // Close Manage Members Modal on ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && manageMembersGroup) {
        setManageMembersGroup(null);
      }
    };

    if (manageMembersGroup) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [manageMembersGroup]);

  // Close Share Modal on ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && shareGroup) {
        setShareGroup(null);
      }
    };

    if (shareGroup) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [shareGroup]);

  // Close Add Favorites Modal on ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showAddFavoritesModal) {
        setShowAddFavoritesModal(false);
      }
    };

    if (showAddFavoritesModal) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [showAddFavoritesModal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      const isClickInsideMenu = target.closest('[data-group-menu]');
      const isClickOnGroupButton = target.closest('[data-group-button]');

      if (!isClickInsideMenu && !isClickOnGroupButton) {
        setShowGroupMenu(null);
      }
    };

    if (showGroupMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showGroupMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      const isClickInsideSortMenu = target.closest('[data-sort-menu]');
      const isClickOnSortButton = target.closest('[data-sort-button]');
      const isClickInsideFavoritesSortMenu = target.closest('[data-favorites-sort-menu]');
      const isClickOnFavoritesSortButton = target.closest('[data-favorites-sort-button]');

      const isClickInsideCollectionFilter = target.closest('[data-collection-filter]');
      const isClickOnCollectionButton = target.closest('[data-collection-button]');

      const isClickInsideModal = target.closest('[data-collection-info-modal]');

      if (!isClickInsideSortMenu && !isClickOnSortButton) {
        setShowSortMenu(false);
      }

      if (!isClickInsideFavoritesSortMenu && !isClickOnFavoritesSortButton) {
        setShowFavoritesSortMenu(false);
      }

      if (!isClickInsideCollectionFilter && !isClickOnCollectionButton) {
        setShowCollectionFilter(false);
      }

      if (!isClickInsideModal && Object.keys(openModals).length > 0) {
        const clickedModalId = Object.keys(openModals).find(id => {
          const modalElement = document.querySelector(`[data-modal-id="${id}"]`);
          return modalElement?.contains(target);
        });

        if (!clickedModalId) {
          setOpenModals({});
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showSortMenu, showFavoritesSortMenu, showCollectionFilter, openModals]);

  // Close mobile filter drawer when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isMobileFilterOpen) {
        setIsMobileFilterOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileFilterOpen]);

  // useEffect(() => {
  // setCurrentPage(1);
  // }, [activeTab]);

  // Close mobile filter drawer when window is resized to desktop size
  // useEffect(() => {
  //   const handleResize = () => {
  //     if (window.innerWidth >= 1024) {
  //       setIsMobileFilterOpen(false);
  //     }
  //   };

  //   window.addEventListener('resize', handleResize);
  //   return () => window.removeEventListener('resize', handleResize);
  // }, []);


  const loadFavorites = async () => {
    try {
      const response = await userService.getFavoriteUsers(0, 100, {}, '');
      // const response = await fetch('/api/v1/favorites/users', {
      //   method: 'GET',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      //   }
      // });

      if (response) {
        // const data = await response.json();
        const favoriteUuids = response.users || response || [];
        setFavoritesUsers(favoriteUuids);
        // setFavoritesUsers(new Set(favoriteUuids));
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationParam = async (loadData: boolean) => {
    if (loadData) {
      await Promise.all([
        loadFavorites(),
        loadGroups(),
        loadMasterSkills()
      ]);
      const hasVisited = localStorage.getItem('member_directory_visited');
      if (!hasVisited) {
        setShowGuideCard(true);
        saveStorageNotification('member_directory', 1);
        localStorage.setItem('member_directory_visited', 'true');
      }
    }
    const urlParams = new URLSearchParams(window.location.search);
    console.log('urlParams', urlParams);
    const collectionId = urlParams.get('collection');
    const tab = urlParams.get('tab');
    setOpenModals({});
    if (tab) {
      if (collectionId) {
        loadAndOpenCollection(parseInt(collectionId));
      } else {
        setViewingCollection(null);
        setActiveTab(tab);
      }
    } else {
      setActiveTab('all');
    }
  }
  const loadAndOpenCollection = async (collectionId: number) => {
    setLoadingCollection(true);
    try {
      const response = await userService.getGroupDetails(collectionId);
      // const response = await fetch(`/groups/${collectionId}`, {
      //   headers: {
      //     'Accept': 'application/json',
      //     'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      //   },
      //   credentials: 'same-origin'
      // });
      console.log('loadAndOpenCollection', response)

      if (response) {
        // const data = await response.json();
        const data = response;
        setViewingCollection(data);
        let _groups = [...groups];
        let groupIndex = _groups.findIndex((item) => item.id == data.id);
        if (groupIndex >= 0) {
          _groups[groupIndex] = {
            ..._groups[groupIndex],
            title: data.title,
            description: data.description,
            status: data.status,
            isOwner: data.isOwner,
            shared: data.shared
          };
          setGroups(_groups);
          if(data.isOwner) {
            setshowSharedCollection(true);
          }
        }
        // setActiveTab('favorites');
      } else if (response.status === 403) {
        showNotification('You do not have access to this collection', 'error');
        window.history.pushState({}, '', '/users/directory');
      } else {
        showNotification('Collection not found', 'error');
        window.history.pushState({}, '', '/users/directory');
      }
    } catch (error) {
      console.error('Error loading collection:', error);
      showNotification('Error loading collection', 'error');
      window.history.pushState({}, '', '/users/directory');
    } finally {
      setLoadingCollection(false);
    }
  };

  const handleBackToDirectory = () => {
    setViewingCollection(null);
    window.history.pushState({}, '', '/users/directory');
  };

  const handleCollectionClick = (group: Group) => {
    // Load fresh collection data to ensure isOwner is correct
    loadAndOpenCollection(group.id);
    window.history.pushState({}, '', `/users/directory?tab=collections&collection=${group.id}`);
  };

  const loadGroups = async () => {
    try {
      // loadGroupMembers();
      const response = await fetch('/groups/groups_json', {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setGroups(data);
      setOpenModals(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(groupId => {
          const freshGroup = data.find(g => g.id === parseInt(groupId));
          if (freshGroup) {
            updated[groupId] = {
              ...updated[groupId],
              group: freshGroup
            };
          }
        });
        return updated;
      });
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroups([]);
    }
  };
  const loadMasterSkills = async () => {
    try {
      setLoading(true);
      const response = await userSkillsService.searchMasterSkills('');
      if (response.success) {
        // Filter out already selected skills
        // const selectedIds = skills.map((s) => s.master_special_skill_id).filter(Boolean);
        // let filtered = response.skills.filter((s) => !selectedIds.includes(s.id));
        let filtered = response.skills;
        filtered = [...filtered].sort((a, b) => {
          const nameComparison = a.name.localeCompare(b.name);
          if (nameComparison === 0) {
            return a.sub_skill.localeCompare(b.sub_skill);
          }
          return nameComparison;
        });
        setMasterSkills(filtered);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const toggleFavorite = async (userUuid: string, event: React.MouseEvent, user: any) => {
    event.preventDefault();
    event.stopPropagation();

    const isFavoritedIndex = favoritesUsers.findIndex((item) => item.uuid == userUuid);
    const isFavorited = isFavoritedIndex >= 0;
    const method = isFavorited ? 'DELETE' : 'POST';
    const url = isFavorited ? `/api/v1/favorites/${userUuid}` : '/api/v1/favorites';

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: method === 'POST' ? JSON.stringify({ member_uuid: userUuid }) : undefined
      });

      if (response.ok) {
        setFavoritesUsers(prev => {
          const newFavorites = [...prev]
          if (isFavorited) {
            newFavorites.splice(isFavoritedIndex, 1);
          } else {
            newFavorites.push(user);
          }
          // const newFavorites = new Set(prev);
          // if (isFavorited) {
          //   newFavorites.delete(userUuid);
          // } else {
          //   newFavorites.add(userUuid);
          // }
          return newFavorites;
        });
        if (isFavorited) {
          await loadGroups();
        }
      } else {
        console.error('Error toggling favorite:', response.statusText);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleAddToGroup = async (userUuid: string, groupId: number) => {
    setIsCreatingGroup(true);
    try {
      const response = await fetch(`/groups/${groupId}/add_members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ user_uuid: userUuid })
      });

      if (response.ok) {
        const user = [...users, ...filteredUsers].find(u => u.uuid === userUuid);
        showNotification(`${user?.firstName || ''} ${user?.lastName || ''} added to collection!`, 'success');

        let _groups = [...groups];
        let groupIndex = _groups.findIndex((item) => item.id == groupId);
        if (groupIndex >= 0) {
          const updatedGroup = { ..._groups[groupIndex] };
          if (updatedGroup.members) {
            updatedGroup.members = [...updatedGroup.members, userUuid];
          } else {
            updatedGroup.members = [userUuid];
          }

          if (!updatedGroup.teamMemberAvatars) {
            updatedGroup.teamMemberAvatars = [];
          }
          if (user) {
            const avatarToAdd = user.croppedAvatarUrl || defaultAvatar;
            if (updatedGroup.teamMemberAvatars.length < 5) {
              updatedGroup.teamMemberAvatars = [...updatedGroup.teamMemberAvatars, avatarToAdd as string];
            }
          }
          updatedGroup.teamMembers = (updatedGroup.teamMembers || 0) + 1;

          _groups[groupIndex] = updatedGroup;
          setGroups(_groups);
        }
        // setUserCollections(prev => ({
        //   ...prev,
        //   [userUuid]: [...(prev[userUuid] || []), groupId]
        // }));
      } else {
        const data = await response.json();
        showNotification(data.error || 'Unable to add member', 'error');
      }
    } catch (error) {
      console.error('Error adding to group:', error);
      showNotification('Something went wrong while adding the member.', 'error');
    } finally {
      setIsCreatingGroup(false);
      setShowGroupMenu(null);
    }
  };

  const openCreateModal = (userUuid?: string) => {
    setSelectedUserForGroup(userUuid || null);
    setForm({
      title: '',
      description: '',
      project_type: '',
      status: 'Active',
      start_date: '',
      due_date: '',
      hashtag: '',
      member_ids: userUuid ? [userUuid] : []
    });
    setIsModalOpen(true);
  };

  const handleGroupIconClick = (userUuid: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowGroupMenu(showGroupMenu === userUuid ? null : userUuid);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveDescription = async (groupId: number, description: string) => {
    try {
      const response = await fetch(`/groups/${groupId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          group: {
            description: description
          }
        })
      });

      if (response.ok) {
        await loadGroups();
        setEditingDescriptionId(null);
        showNotification('Description updated successfully!', 'success');
      } else {
        console.error('Failed to update description');
        showNotification('Failed to update description', 'error');
      }
    } catch (error) {
      console.error('Error updating description:', error);
      showNotification('Error updating description', 'error');
    }
  };
  const handleSaveTitle = async (groupId: number, title: string) => {
    try {
      if (title.trim().length <= 0) {
        return;
      }
      const response = await fetch(`/groups/${groupId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          group: {
            title: title
          }
        })
      });
      console.log(response);
      if (response.ok) {
        const data = await response.json();
        // if ('group' in data) {
        //   onSave(data.group);
        // }
        await loadGroups();
        seteditingTitleId(null);
        showNotification('Title updated successfully!', 'success');
      } else {
        console.error('Failed to update Title');
        showNotification('Failed to update Title', 'error');
      }
    } catch (error) {
      console.error('Error updating Title:', error);
      showNotification('Error updating Title', 'error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const formData = {
        group: {
          title: form.title,
          description: form.description,
          project_type: form.project_type,
          status: form.status,
          start_date: form.start_date,
          hashtag: form.hashtag,
          due_date: form.due_date
        },
        member_ids: form.member_ids
      };

      const response = await fetch('/groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await loadGroups();
        setIsModalOpen(false);
        showNotification('Collection created successfully!', 'success');
      } else {
        const errorData = await response.json();
        console.error('Failed to create group:', errorData);
        showNotification(`Failed to create collection: ${errorData.errors ? errorData.errors.join(', ') : 'Unknown error'}`, 'error');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      showNotification('An error occurred while creating the collection. Please try again.', 'error');
    }
  };

  const handleGroupInfoClick = (group: Group, groupId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    const cardElement = cardRefs.current[groupId];
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setOpenModals(prev => ({
        ...prev,
        [groupId]: {
          group,
          position: {
            top: rect.top + scrollTop,
            left: rect.left
          }
        }
      }));
    }
  };

  const handleCloseModal = (groupId: number) => {
    setOpenModals(prev => {
      const newModals = { ...prev };
      delete newModals[groupId];
      return newModals;
    });
  };

  const handleUnarchive = async (groupId: number) => {
    try {
      const response = await fetch(`/groups/${groupId}/unarchive`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (response.ok) {
        await loadGroups();
        handleCloseModal(groupId);
        showNotification('Collection restored successfully!', 'success');
      } else {
        console.error('Failed to unarchive group');
        showNotification('Failed to restore collection. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error unarchiving group:', error);
      showNotification('An error occurred while restoring the collection.', 'error');
    }
  };

  const handleArchive = async (groupId: number) => {
    try {
      const response = await fetch(`/groups/${groupId}/archive`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (response.ok) {
        await loadGroups();
        handleCloseModal(groupId);
        showNotification('Collection archived successfully!', 'success');
      } else {
        console.error('Failed to archive group');
        showNotification('Failed to archive collection. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error archiving group:', error);
      showNotification('An error occurred while archiving the collection.', 'error');
    }
  };

  const fetchGroupMembers = async (groupId: number): Promise<User[]> => {
    try {
      const response = await fetch(`/groups/${groupId}/members`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.members || [];
    } catch (err) {
      console.error('Error fetching group members:', err);
      return [];
    }
  };

  const [showGuideCard, setShowGuideCard] = useState(
    getStorageNotification('member_directory') == 1 ? true : false
  );

  // const fetchAvailableUsers = async (): Promise<User[]> => {
  //   try {
  //     const response = await fetch('/users/share_index', {
  //       headers: {
  //         'Accept': 'application/json',
  //         'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  //       },
  //       credentials: 'same-origin'
  //     });
  //     const data = await response.json();
  //     return (data.users || []).filter(user => user.uuid !== currentUser.uuid);
  //   } catch (err) {
  //     console.error('Error fetching available users:', err);
  //     return [];
  //   }
  // };

  const fetchAvailableUsersForShare = async (keyword: string): Promise<User[]> => {
    try {
      const response = await fetch('/users/share_index' + (keyword != '' ? '?keyword=' + keyword : ''), {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });
      const data = await response.json();
      console.log('fetchAvailableUsersForShare: All users from API:', data.users);
      const filteredUsers = (data.users || []).filter(user =>
        user.uuid !== currentUser.uuid
        // && (user.superAdmin === true ||user.admin === true)
      );

      console.log('fetchAvailableUsersForShare: Filtered users for share:', filteredUsers);
      return filteredUsers;
    } catch (err) {
      console.error('Error fetching available users for share:', err);
      return [];
    }
  };

  const fetchGroupSharedUsers = async (groupId: number): Promise<User[]> => {
    try {
      const response = await fetch(`/groups/${groupId}/shared_users`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.users || [];
    } catch (err) {
      console.error('Error fetching shared users:', err);
      return [];
    }
  };

  const handleOpenShareModal = async (group: Group) => {
    handleCloseModal(group.id);
    setShareGroup(group);
    setShareSearchTerm('');
    setSelectedShareUsers([]);

    const [allUsers, sharedUsers] = await Promise.all([
      fetchAvailableUsersForShare(''),
      fetchGroupSharedUsers(group.id)
    ]);

    setCurrentlySharedWith(sharedUsers);
    const sharedUserUuids = new Set(sharedUsers.map(u => u.uuid));
    const filteredUsers = allUsers.filter(user =>
      !sharedUserUuids.has(user.uuid) && user.uuid !== currentUser.uuid
    );
    setAvailableShareUsers(filteredUsers);
  };

  const searchShareUsers = async (keyword: string) => {
    // if(shareSearchTerm.trim().length > 0) {
    const allUsers = await fetchAvailableUsersForShare(keyword);
    const sharedUserUuids = new Set(currentlySharedWith.map(u => u.uuid));
    const filteredUsers = allUsers.filter(user =>
      !sharedUserUuids.has(user.uuid) && user.uuid !== currentUser.uuid
    );
    setAvailableShareUsers(filteredUsers);
    // }
  }

  const handleShareUsers = async () => {
    if (!shareGroup || selectedShareUsers.length === 0) return;

    try {
      const collectionLink = `${window.location.origin}/users/directory?collection=${shareGroup.id}`;

      const response = await fetch(`/groups/${shareGroup.id}/share_with_users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          user_uuids: selectedShareUsers
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const usersToAdd = availableShareUsers.filter(user => selectedShareUsers.includes(user.uuid));
        setCurrentlySharedWith(prev => [...prev, ...usersToAdd]);
        setAvailableShareUsers(prev => prev.filter(user => !selectedShareUsers.includes(user.uuid)));
        setSelectedShareUsers([]);

        await loadGroups();

        // Show notification with shareable link
        showNotification(
          <div className="flex flex-col">
            <span>Collection shared with {selectedShareUsers.length} user(s).</span>

          </div>,
          'success'
        );
      } else {
        console.error('Failed to share with users:', result);
        showNotification(result.error || 'Failed to share collection. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error sharing group:', error);
      showNotification('An error occurred while sharing the collection.', 'error');
    }
  };


  const handleUnshareUser = async (userUuid: string) => {
    if (!shareGroup) return;

    try {
      const response = await fetch(`/groups/${shareGroup.id}/unshare_user/${userUuid}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const userToRemove = currentlySharedWith.find(u => u.uuid === userUuid);
        if (userToRemove) {
          setCurrentlySharedWith(prev => prev.filter(u => u.uuid !== userUuid));
          if (userToRemove.uuid !== currentUser.uuid) {
            setAvailableShareUsers(prev => [...prev, userToRemove]);
          }
        }

        setTimeout(async () => {
          await loadGroups();
        }, 500);
      } else {
        console.error('Failed to unshare with user:', result);
        showNotification(result.error || 'Failed to unshare collection. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error unsharing group:', error);
      showNotification('An error occurred while unsharing the collection.', 'error');
    }
  };

  const toggleShareUserSelection = (uuid: string) => {
    setSelectedShareUsers(prev =>
      prev.includes(uuid)
        ? prev.filter(id => id !== uuid)
        : [...prev, uuid]
    );
  };

  const handleOpenManageMembers = async (group: Group) => {
    handleCloseModal(group.id);
    setManageMembersGroup(group);
    setManageMembersActiveTab(group.isOwner ? 'add' : 'current');
    setSelectedMembers([]);
    setSearchMember('');

    const [members, available] = await Promise.all([
      fetchGroupMembers(group.id),
      group.isOwner ? fetchAvailableUsersForShare('') : Promise.resolve([])
    ]);

    setCurrentMembers(members);
    if (group.isOwner) {
      const memberUuids = new Set(members.map(m => m.uuid));
      const filteredUsers = available.filter(user =>
        !memberUuids.has(user.uuid) && user.uuid !== currentUser.uuid
      );
      setAvailableUsersForManage(filteredUsers);
    }
  };

  const toggleMemberSelection = (uuid: string) => {
    setSelectedMembers(prev =>
      prev.includes(uuid)
        ? prev.filter(id => id !== uuid)
        : [...prev, uuid]
    );
  };

  const handleAddMembersToGroup = async () => {
    if (!manageMembersGroup || selectedMembers.length === 0) return;

    try {
      const response = await fetch(`/groups/${manageMembersGroup.id}/add_members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          member_ids: selectedMembers
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const membersToAdd = availableUsersForManage.filter(user => selectedMembers.includes(user.uuid));
        setCurrentMembers(prev => [...prev, ...membersToAdd]);
        setAvailableUsersForManage(prev => prev.filter(user => !selectedMembers.includes(user.uuid)));
        setSelectedMembers([]);

        if (result.errors && result.errors.length > 0) {
          console.warn('Some members could not be added:', result.errors);
          showNotification(result.message + ' (Some warnings occurred)', 'success');
        }

        let _groups = [...groups];
        let groupIndex = _groups.findIndex((item) => item.id == manageMembersGroup.id);
        if (groupIndex >= 0) {
          const updatedGroup = { ..._groups[groupIndex] };
          if (!updatedGroup.teamMemberAvatars) updatedGroup.teamMemberAvatars = [];
          if (!updatedGroup.members) updatedGroup.members = [];

          updatedGroup.members = [...updatedGroup.members, ...selectedMembers];
          membersToAdd.forEach(user => {
            const avatarToAdd = user.croppedAvatarUrl || defaultAvatar;
            if (updatedGroup.teamMemberAvatars.length < 5) {
              updatedGroup.teamMemberAvatars.push(avatarToAdd as string);
            }
          });
          updatedGroup.teamMembers = (updatedGroup.teamMembers || 0) + membersToAdd.length;
          _groups[groupIndex] = updatedGroup;
          setGroups(_groups);
        }

        await loadFavorites();
        await loadGroups();
      } else {
        console.error('Failed to add members:', result);
        showNotification(result.error || 'Failed to add members. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error adding members:', error);
      showNotification('An error occurred while adding members.', 'error');
    }
  };

  const handleRemoveMemberFromGroup = async (memberUuid: string) => {
    if (!manageMembersGroup) return;

    try {
      const response = await fetch(`/groups/${manageMembersGroup.id}/remove_member/${memberUuid}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        credentials: 'same-origin'
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const memberToRemove = currentMembers.find(m => m.uuid === memberUuid);
        if (memberToRemove) {
          setCurrentMembers(prev => prev.filter(m => m.uuid !== memberUuid));
          if (memberToRemove.uuid !== currentUser.uuid) {
            setAvailableUsersForManage(prev => [...prev, memberToRemove]);
          }

          // Update local collections state
          if (manageMembersGroup) {
            let _groups = [...groups];
            let groupIndex = _groups.findIndex((item) => item.id == manageMembersGroup.id);
            if (groupIndex >= 0) {
              const updatedGroup = { ..._groups[groupIndex] };
              if (updatedGroup.members) {
                const id = updatedGroup.members.indexOf(memberUuid);
                if (id >= 0) {
                  updatedGroup.members.splice(id, 1);
                }
              }
              if (updatedGroup.teamMemberAvatars && memberToRemove.croppedAvatarUrl) {
                const avatarId = updatedGroup.teamMemberAvatars.findIndex(a => a === memberToRemove.croppedAvatarUrl);
                if (avatarId >= 0) {
                  updatedGroup.teamMemberAvatars.splice(avatarId, 1);
                }
              }
              updatedGroup.teamMembers = Math.max(0, (updatedGroup.teamMembers || 1) - 1);
              _groups[groupIndex] = updatedGroup;
              setGroups(_groups);
            }
          }
          // setUserCollections(prev => ({
          //   ...prev,
          //   [memberUuid]: (prev[memberUuid] || []).filter(id => id !== manageMembersGroup.id)
          // }));
        }

        setTimeout(async () => {
          await loadGroups();
        }, 500);
      } else {
        console.error('Failed to remove member:', result);
        showNotification(result.error || 'Failed to remove member. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      showNotification('An error occurred while removing member.', 'error');
    }
  };

  // const FiltersContent = ({
  //   addFavoritesFilters,
  //   setAddFavoritesFilters,
  //   addFavoritesSearchTerm,
  //   setAddFavoritesSearchTerm,
  //   handleClearFilters
  // }: {
  //   addFavoritesFilters: any;
  //   setAddFavoritesFilters: any;
  //   addFavoritesSearchTerm: string;
  //   setAddFavoritesSearchTerm: (term: string) => void;
  //   handleClearFilters: () => void;
  // }) => (
  //   <div className="space-y-4">
  //     {/* From Dropdown */}
  //     <div>
  //       <label className="block text-sm font-medium text-gray-700 mb-2">From:</label>
  //       <select
  //         className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
  //         value={selectedCollectionFilter}
  //         onChange={(e) => {
  //           const value = e.target.value;
  //           if (value === 'all' || value === 'favorites') {
  //             setSelectedCollectionFilter(value);
  //           } else {
  //             const numValue = parseInt(value);
  //             if (!isNaN(numValue)) {
  //               setSelectedCollectionFilter(numValue);
  //             }
  //           }
  //         }}
  //       >
  //         <option value="all">All Members</option>
  //         <option value="favorites">All My Favorites</option>
  //         {groups.filter(g => !isGroupArchived(g)).map(group => (
  //           <option key={group.id} value={group.id}>{group.title}</option>
  //         ))}
  //       </select>
  //     </div>

  //     {/* Search */}
  //     <div>
  //       <label className="block text-sm font-medium text-gray-700 mb-2">Search by:</label>
  //       <div className="relative">
  //         <input
  //           type="text"
  //           placeholder="Keyword..."
  //           value={addFavoritesSearchTerm}
  //           onChange={(e) => setAddFavoritesSearchTerm(e.target.value)}
  //           className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm"
  //         />
  //         <svg className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  //           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  //         </svg>
  //       </div>
  //     </div>

  //     {/* Filter By Header */}
  //     <div className="flex items-center justify-between pt-4">
  //       <label className="block text-sm font-medium text-gray-700">Filter by:</label>
  //       <button
  //         onClick={handleClearFilters}
  //         className="text-xs text-blue-600 hover:text-blue-700"
  //       >
  //         Clear Filters
  //       </button>
  //     </div>

  //     {/* Gender Filter */}
  //     <div>
  //       <label className="block text-xs text-gray-600 mb-1">Gender</label>
  //       <select
  //         value={addFavoritesFilters.gender}
  //         onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, gender: e.target.value }))}
  //         className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
  //       >
  //         <option value="">All</option>
  //         <option value="Male">Male</option>
  //         <option value="Female">Female</option>
  //         <option value="Non-binary">Non-binary</option>
  //       </select>
  //     </div>

  //     {/* Ethnicity Filter */}
  //     <div>
  //       <label className="block text-xs text-gray-600 mb-1">Ethnicity</label>
  //       <select
  //         value={addFavoritesFilters.ethnicity}
  //         onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, ethnicity: e.target.value }))}
  //         className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
  //       >
  //         <option value="">All</option>
  //         <option value="Caucasian">Caucasian</option>
  //         <option value="African American">African American</option>
  //         <option value="Asian">Asian</option>
  //         <option value="Hispanic">Hispanic</option>
  //         <option value="Middle Eastern">Middle Eastern</option>
  //         <option value="Pacific Islander">Pacific Islander</option>
  //         <option value="Native American">Native American</option>
  //       </select>
  //     </div>

  //     {/* Country Filter */}
  //     <div>
  //       <label className="block text-xs text-gray-600 mb-1">Country</label>
  //       <select
  //         value={addFavoritesFilters.country}
  //         onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, country: e.target.value }))}
  //         className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
  //       >
  //         <option value="">All</option>
  //         <option value="Australia">Australia</option>
  //         <option value="France">France</option>
  //         <option value="Italy">Italy</option>
  //         <option value="USA">USA</option>
  //         <option value="UK">UK</option>
  //         <option value="Canada">Canada</option>
  //       </select>
  //     </div>

  //     {/* Weight Filter */}
  //     <div>
  //       <label className="block text-xs text-gray-600 mb-1">Weight</label>
  //       <div className="flex items-center gap-2">
  //         <input
  //           type="number"
  //           value={addFavoritesFilters.weightMin}
  //           onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, weightMin: Number(e.target.value) }))}
  //           className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
  //           placeholder="Min"
  //         />
  //         <span className="text-gray-500">-</span>
  //         <input
  //           type="number"
  //           value={addFavoritesFilters.weightMax}
  //           onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, weightMax: Number(e.target.value) }))}
  //           className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
  //           placeholder="Max"
  //         />
  //       </div>
  //       <span className="text-xs text-gray-500 mt-1 block">Lbs</span>
  //     </div>

  //     {/* Height Filter */}
  //     <div>
  //       <label className="block text-xs text-gray-600 mb-1">Height</label>
  //       <div className="flex items-center gap-2">
  //         <input
  //           type="number"
  //           value={addFavoritesFilters.heightMin}
  //           onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, heightMin: Number(e.target.value) }))}
  //           className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
  //           placeholder="Min"
  //         />
  //         <span className="text-gray-500">-</span>
  //         <input
  //           type="number"
  //           value={addFavoritesFilters.heightMax}
  //           onChange={(e) => setAddFavoritesFilters(prev => ({ ...prev, heightMax: Number(e.target.value) }))}
  //           className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
  //           placeholder="Max"
  //         />
  //       </div>
  //       <span className="text-xs text-gray-500 mt-1 block">Ft</span>
  //     </div>

  //     {/* Add More Filters Button */}
  //     <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 mt-4">
  //       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  //         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  //       </svg>
  //       Add More Filters
  //     </button>
  //   </div>
  // );


  const displayedUsers = activeTab === 'all'
    ? filteredUsers
    : filteredUsers.filter(user => favoritesUsers.findIndex((item) => item.uuid == user.uuid) >= 0 //favoritesUsers.has(user.uuid)
    );

  const sortedUsers = [...displayedUsers].sort((a, b) => {
    switch (sortOption) {
      case 'favorites':
        const aFav = favoritesUsers.findIndex((item) => item.uuid == a.uuid) >= 0 //favoritesUsers.has(a.uuid)
          ? 1 : 0;
        const bFav = favoritesUsers.findIndex((item) => item.uuid == b.uuid) >= 0 //favoritesUsers.has(b.uuid) 
        // ? 1 : 0;
        return bFav - aFav;
      case 'newest':
        return b.id - a.id;
      case 'oldest':
        return a.id - b.id;
      case 'name-asc':
        return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
      case 'name-desc':
        return `${b.firstName} ${b.lastName}`.localeCompare(`${a.firstName} ${a.lastName}`);
      default:
        return 0;
    }
  });

  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = sortedUsers.slice(startIndex, endIndex);

  // const getPageNumbers = () => {
  //   const pages: (number | string)[] = [];
  //   const maxVisiblePages = 5;

  //   if (totalPages <= maxVisiblePages + 2) {
  //     for (let i = 1; i <= totalPages; i++) {
  //       pages.push(i);
  //     }
  //   } else {
  //     pages.push(1);

  //     if (currentPage > 3) {
  //       pages.push('...');
  //     }

  //     const start = Math.max(2, currentPage - 1);
  //     const end = Math.min(totalPages - 1, currentPage + 1);

  //     for (let i = start; i <= end; i++) {
  //       pages.push(i);
  //     }

  //     if (currentPage < totalPages - 2) {
  //       pages.push('...');
  //     }

  //     pages.push(totalPages);
  //   }

  //   return pages;
  // };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isGroupArchived = (group: Group) => {
    return group.status === 'Archived' || group.archived === true;
  };

  const filteredGroups = groups.filter(group => {
    const archived = isGroupArchived(group);
    const active = !archived;

    if (collectionFilter === 'active') {
      return active;
    } else if (collectionFilter === 'archived') {
      return archived;
    }
    return true;
  });

  const allAvailableUsers = React.useMemo(() => {
    const combined = [...users, ...filteredUsers];
    const uniqueUsers = combined.filter((user, index, self) =>
      self.findIndex(u => u.uuid === user.uuid) === index
    );
    return uniqueUsers;
  }, [users, filteredUsers]);

  const favoritedUsers = favoritesUsers //  allAvailableUsers.filter(user => favoritesUsers.has(user.uuid));

  const sortedFavoritedUsers = [...favoritedUsers].sort((a, b) => {
    switch (favoritesSortOption) {
      case 'newest':
        return b.id - a.id;
      case 'oldest':
        return a.id - b.id;
      case 'name-asc':
        return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
      case 'name-desc':
        return `${b.firstName} ${b.lastName}`.localeCompare(`${a.firstName} ${a.lastName}`);
      default:
        return 0;
    }
  });

  useEffect(() => {
    const loadFilteredUsers = async () => {
      if (!showAddFavoritesModal) return;

      setIsLoadingFilteredUsers(true);


      try {
        // const filtered = await getFilteredUsersForAddFavorites();
        // console.log('Filtered users count:', filtered.length);
        // setFilteredUsersForFavorites(filtered);
      } catch (error) {
        console.error('Error loading filtered users:', error);
        // setFilteredUsersForFavorites([]);
      } finally {
        setIsLoadingFilteredUsers(false);
      }
    };

    loadFilteredUsers();
  }, [showAddFavoritesModal, addFavoritesSearchTerm, addFavoritesFilters, favoritesUsers, allAvailableUsers]);

  // const getFilteredUsersForAddFavorites = async () => {
  // let baseUsers = [...allAvailableUsers];
  // if (selectedCollectionFilter === 'favorites') {
  //   baseUsers = baseUsers.filter(user => favoritesUsers.findIndex((item) => item.uuid == user.uuid) >= 0); //favoritesUsers.has(user.uuid));
  //   console.log('After favorites filter:', baseUsers.length);
  // } else if (selectedCollectionFilter !== 'all' && typeof selectedCollectionFilter === 'number') {
  //   console.log('Fetching members for collection:', selectedCollectionFilter);
  //   try {
  //     const members = await fetchGroupMembers(selectedCollectionFilter);
  //     console.log('Fetched collection members:', members.length);
  //     const memberUuids = new Set(members.map(m => m.uuid));
  //     baseUsers = baseUsers.filter(user => memberUuids.has(user.uuid));
  //     console.log('After collection filter:', baseUsers.length);
  //   } catch (error) {
  //     console.error('Error fetching collection members:', error);
  //     baseUsers = [];
  //   }
  // }

  // const filtered = baseUsers.filter(user => {
  // if (selectedCollectionFilter === 'all' && favoritesUsers.findIndex((item) => item.uuid == user.uuid) >= 0) {//) {
  //   return false;
  // }

  // if (addFavoritesSearchTerm) {
  //   const searchLower = addFavoritesSearchTerm.toLowerCase();
  //   const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
  //   if (!fullName.includes(searchLower)) return false;
  // }

  // if (addFavoritesFilters.gender && user.gender !== addFavoritesFilters.gender) {
  //   return false;
  // }

  // if (addFavoritesFilters.ethnicity) {
  //   if (!user.ethnicities?.includes(addFavoritesFilters.ethnicity)) {
  //     return false;
  //   }
  // }

  // if (addFavoritesFilters.country && user.country !== addFavoritesFilters.country) {
  //   return false;
  // }

  // if (user.weight < addFavoritesFilters.weightMin || user.weight > addFavoritesFilters.weightMax) {
  //   return false;
  // }

  // if (user.height < addFavoritesFilters.heightMin || user.height > addFavoritesFilters.heightMax) {
  //   return false;
  // }

  // return true;
  // });
  // return filtered;
  // };

  const toggleUserFavoriteSelection = (uuid: string, user: any) => {
    setSelectedUsersToFavorite(prev =>
      prev.includes(uuid)
        ? prev.filter(id => id !== uuid)
        : [...prev, uuid]
    );
  };

  const handleAddMultipleFavorites = async () => {
    if (selectedUsersToFavorite.length === 0) return;

    try {
      const promises = selectedUsersToFavorite.map(uuid =>
        fetch('/api/v1/favorites', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          },
          body: JSON.stringify({ member_uuid: uuid })
        })
      );

      await Promise.all(promises);

      setFavoritesUsers(prev => {
        const newFavorites = [...prev]
        selectedUsersToFavorite.forEach(uuid => {
          const userTemp = allAvailableUsers.find((item) => item.uuid == uuid)
          if (userTemp) {
            newFavorites.push(userTemp)
          }
        })
        // if (isFavorited) {
        //   newFavorites.splice(isFavoritedIndex, 1);
        // } else {
        //   newFavorites.push(user);
        // }

        // const newFavorites = new Set(prev);
        // selectedUsersToFavorite.forEach(uuid => newFavorites.add(uuid));
        return newFavorites;
      });

      setShowAddFavoritesModal(false);
      setSelectedUsersToFavorite([]);

      showNotification(`${selectedUsersToFavorite.length} member(s) added to favorites!`, 'success');
    } catch (error) {
      console.error('Error adding favorites:', error);
      showNotification('Error adding favorites. Please try again.', 'error');
    }
  };

  const handleClearFilters = () => {
    setAddFavoritesFilters({
      gender: '',
      ethnicity: '',
      country: '',
      weightMin: measurementRanges.weight.min,
      weightMax: measurementRanges.weight.max,
      heightMin: measurementRanges.height.min,
      heightMax: measurementRanges.height.max,
    });
  };

  const favoritesTotalPages = Math.ceil(sortedFavoritedUsers.length / itemsPerPage);
  const favoritesStartIndex = (currentPage - 1) * itemsPerPage;
  const favoritesEndIndex = currentPage * itemsPerPage;
  const paginatedFavoritedUsers = sortedFavoritedUsers.slice(0, favoritesEndIndex);
  const favoritesPageNumbers = (() => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    if (favoritesTotalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= favoritesTotalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(favoritesTotalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < favoritesTotalPages - 2) pages.push('...');
      pages.push(favoritesTotalPages);
    }
    return pages;
  })();

  const countGroups = () => {
    const active = groups.filter(g => !isGroupArchived(g)).length;
    const archived = groups.filter(g => isGroupArchived(g)).length;
    return { active, archived, total: groups.length };
  };

  const groupCounts = countGroups();

  const showGuideUi = () => {
    return (
      <div className="w-full mb-6 p-5 bg-blue-100 text-blue-800 rounded-lg relative">
        {/* Close Button */}
        <button
          id="member_directory_close_btn"
          className="absolute top-2 right-2 cursor-pointer w-5 h-5 flex items-center justify-center text-blue-800 hover:text-blue-900"
          onClick={() => {
            setShowGuideCard(false);
            saveStorageNotification('member_directory', 0);
          }}
          aria-label="Close information panel"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Main Title */}
        <div className="text-base font-semibold mb-4">
          About Filmik's Member Directory
        </div>

        {/* Content */}
        <div className="text-base font-normal space-y-3">
          <p>
            FILMIK’s Exclusive Member Directory is a private, coordinator-only directory built for staffing stunt teams within FILMIK.
          </p>
          <p>
            Every performer listed has been onboarded through real productions by top coordinators using the platform.
          </p>
          <p>
            Search, favorite, and organize talent into collections, review submission kits, and move performers directly into your projects
            and submission boards — all in one integrated workflow.
          </p>
        </div>
      </div>
    );
  };



  const isInGroup = (user_uuid) => {
    return groups.filter((group) => group.members && group.members.indexOf(user_uuid) >= 0).length > 0;
  }

  const [currentHashtagInput, setCurrentHashtagInput] = useState('');
  const handleHashtagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = currentHashtagInput.trim();

      if (val) {
        setForm(prev => {
          const currentTags = prev.hashtag ? prev.hashtag.split('  ').filter(t => t.trim() !== '') : [];
          const newTag = val.startsWith('#') ? val : `#${val}`;
          if (!currentTags.includes(newTag)) {
            return { ...prev, hashtag: [...currentTags, newTag].join('  ') + '  ' };
          }
          return prev;
        });
        setCurrentHashtagInput('');
      }
    }
  };

  const removeHashtag = (tagToRemove: string) => {
    setForm(prev => {
      const currentTags = prev.hashtag ? prev.hashtag.split('  ').filter(t => t.trim() !== '') : [];
      const updatedTags = currentTags.filter(tag => tag !== tagToRemove);
      return { ...prev, hashtag: updatedTags.length > 0 ? updatedTags.join('  ') + '  ' : '' };
    });
  };

  if (loading && filteredUsers.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500">Loading directory...</div>
      </div>
    );
  }
  if (loadingCollection) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 mx-auto text-blue-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-500">Loading collection...</p>
        </div>
      </div>
    );
  }
  const addFavUserFilterMobileTop = () => {
    return <>
      {/* Mobile Filter Toggle Fav */}
      {/* <div className="lg:hidden border-b border-gray-200 flex-shrink-0">
              <button
                onClick={() => setIsMobileFiltersOpenFav(!isMobileFiltersOpenFav)}
                className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <span>Filters</span>
                <svg
                  className={`w-5 h-5 transform transition-transform ${isMobileFiltersOpenFav ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div> */}
    </>;
  }
  const addFavUserFilterDesktop = () => {
    return <div className="hidden lg:block w-80 p-6  flex-shrink-0">
      <div className="w-64  mx-auto flex max-w-7xl items-left justify-between">
        <div className="space-y-4">


          <div className="w-full text-sm font-medium text-gray-700">
            Search Profiles:
          </div>
          <UserFilter
            openFilters={false}
            firstnameLastNameshow={false}
            keywordshow={true}
            userGroupshow={true}
            userGroupsAll={[...groups.map((item) => {
              return { id: item.id, name: item.title };
            })]}

            users={[]}
            // measurementRanges={measurementRanges}
            measurementRanges={{
              height: {
                min: 91, // 3ft : 91.44cm
                max: 244, // 8ft: 243.84cm
              },
              weight: {
                min: 22, // 50:lbs = 22.6796kg
                max: 182, // 400:lbs = 181.437kg
              },
            }}
            currentUser={currentUser}
            onFilter={(filteredUsers, filterParams: any) => {
              console.log('loadmore: filterParams3', filterParams)
              setfilteredParams(filterParams)
            }}
            showFavoritesOnly={showFavoritesOnly}
            // favorites={favoritesUsers}
            masterSkills={masterSkills}
            onClose={function (): void {
              setIsMobileFiltersOpenFav(false);
            }}
          />
        </div>
      </div>

      {/* <FiltersContent 
                  addFavoritesFilters={addFavoritesFilters}
                  setAddFavoritesFilters={setAddFavoritesFilters}
                  addFavoritesSearchTerm={addFavoritesSearchTerm}
                  setAddFavoritesSearchTerm={setAddFavoritesSearchTerm}
                  handleClearFilters={handleClearFilters}
                /> */}
    </div>
      ;
  }
  const addFavUserFilterMobile = () => {
    return <>
      {isMobile &&  true && (
        <>
          {/* <div className="hidden sm:block p-4 space-y-4 bg-white border-b border-gray-200">
            <div className="w-full text-sm font-medium text-gray-700">
              Search Profiles:
            </div>
          </div> */}
          <UserFilter
            openFilters={isMobileFiltersOpenFav}
            firstnameLastNameshow={false}
            keywordshow={true}
            userGroupshow={true}
            userGroupsAll={[...groups.map((item) => {
              return { id: item.id, name: item.title };
            })]}
            users={users}
            measurementRanges={{
              height: {
                min: 91, // 3ft : 91.44cm
                max: 244, // 8ft: 243.84cm
              },
              weight: {
                min: 22, // 50:lbs = 22.6796kg
                max: 182, // 400:lbs = 181.437kg
              },
            }}
            currentUser={currentUser}
            // onFilter={setFilteredUsers}
            onFilter={(filteredUsers, filterParams: any) => {
              console.log('loadmore: filterParams1', filterParams)
              setfilteredParams(filterParams);
            }}
            showFavoritesOnly={showFavoritesOnly}
            masterSkills={masterSkills}
            onClose={function (): void {
              setIsMobileFiltersOpenFav(false);
            }}
          />
        </>
      )}
    </>
      ;
  }
  const addMembersUserFilterDesktop = () => {
    return <>
      {!isMobile && !isMobileFilterOpen && <div
        className={`hidden lg:block overflow-visible  ${activeTab === 'all' ? 'lg:w-64 opacity-100' : 'w-0 opacity-0'
          }`}
      >
        <div className="w-64 space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Search Profiles:</h3>
            <UserFilter
              openFilters={false}
              firstnameLastNameshow={false}
              keywordshow={true}
              userGroupshow={true}
              userGroupsAll={[...groups.map((item) => {
                return { id: item.id, name: item.title };
              })]}

              users={[]}
              // measurementRanges={measurementRanges}
              measurementRanges={{
                height: {
                  min: 91, // 3ft : 91.44cm
                  max: 244, // 8ft: 243.84cm
                },
                weight: {
                  min: 22, // 50:lbs = 22.6796kg
                  max: 182, // 400:lbs = 181.437kg
                },
              }}
              currentUser={currentUser}
              onFilter={(filteredUsers, filterParams: any) => {
                // setFilteredUsers()
                console.log('loadmore: filterParams2', filterParams)
                // Handle all cases: filterPresent == 0 (reset), 1 (filters active), 2 (clear all)
                setfilteredParams(filterParams)
              }}
              showFavoritesOnly={showFavoritesOnly}
              masterSkills={masterSkills}
              // favorites={favoritesUsers}
              onClose={function (): void {
                setIsMobileFilterOpen(false);
              }}
            />
          </div>
        </div>
      </div>}

    </>;
  }
  const addMembersUserFilterMobile = () => {
    return <>
      {isMobile &&  (
        <>
          <UserFilter
            openFilters={isMobileFilterOpen}
            firstnameLastNameshow={false}
            keywordshow={true}
            userGroupshow={true}
            userGroupsAll={[...groups.map((item) => {
              return { id: item.id, name: item.title };
            })]}
            users={users}
            measurementRanges={{
              height: {
                min: 91, // 3ft : 91.44cm
                max: 244, // 8ft: 243.84cm
              },
              weight: {
                min: 22, // 50:lbs = 22.6796kg
                max: 182, // 400:lbs = 181.437kg
              },
            }}
            currentUser={currentUser}
            onFilter={(filteredUsers, filterParams: any) => {
              console.log('loadmore: filterParams1', filterParams)
              setfilteredParams(filterParams);
            }}
            showFavoritesOnly={showFavoritesOnly}
            masterSkills={masterSkills}
            onClose={function (): void {
              setIsMobileFilterOpen(false);
            }}
          // favorites={favoritesUsers}
          />
        </>
      )}
    </>;
  }
  const addFavRender = () => {
    return <div className="fixed inset-0 bg-customGray flex items-center justify-center z-50 px-2 pb-2 sm:px-4 sm:pb-4 pt-20"
      // style={{ backgroundColor: '#6b7a8a' }}
      onClick={() => setShowAddFavoritesModal(false)}>
      <div className="bg-white rounded-xl shadow-xl flex flex-col overflow-hidden w-full h-full"
        style={{
          width: '100%',
          height: '100%', maxWidth: '1170px', maxHeight: '90vh',
          ...(window.innerWidth >= 1024 && {
            width: '1170px',
            height: '90vh'
          })
        }}
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
            Add Favorites
          </h2>
          <button
            onClick={() => setShowAddFavoritesModal(false)}
            className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {
          addFavUserFilterMobileTop()
        }
        {/* Main Content Fav */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar - Filters - Desktop */}
          {!isMobile && !isMobileFiltersOpenFav && (
            addFavUserFilterDesktop()
          )}

          {/* Mobile Filters Overlay */}
          {
            addFavUserFilterMobile()
          }

          {/* Right Side - Results */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Results Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Results
              </h3>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <button
                    onClick={() => setShowAddFavSortMenu(!showAddFavSortMenu)}
                    className="p-2 border border-gray-300 hover:bg-gray-100 rounded-lg flex items-center justify-center"
                    style={{ width: '2.25rem', height: '2.25rem' }}
                    title="Sort members"
                  >
                    <ArrowDownNarrowWide className="w-5 h-5 text-gray-600" />
                  </button>

                  {showAddFavSortMenu && (
                    <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <button
                        onClick={() => {
                          setSortOption('newest');
                          setShowAddFavSortMenu(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'newest' ? 'text-blue-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        New Members First
                      </button>
                      <button
                        onClick={() => {
                          setSortOption('oldest');
                          setShowAddFavSortMenu(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'oldest' ? 'text-blue-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Old Members First
                      </button>
                      <button
                        onClick={() => {
                          setSortOption('name-asc');
                          setShowAddFavSortMenu(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'name-asc' ? 'text-blue-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        Name (A-Z)
                      </button>
                    </div>
                  )}
                </div>

                {/* Mobile Filter Button */}
                <button
                  onClick={() => setIsMobileFiltersOpenFav(true)}
                  className="lg:hidden p-2 hover:bg-gray-100 rounded border border-gray-300"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Results Grid - Scrollable */}
            <div className="flex-1 overflow-y-auto pt-3 sm:pr-6">
              {isLoadingFilteredUsers ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <svg className="animate-spin h-8 w-8 mx-auto text-blue-600 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-sm text-gray-500">Loading members...</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
                    {filteredUsers.map((user) => {
                      const isSelected = selectedUsersToFavorite.includes(user.uuid);
                      return (
                        <div
                          key={user.uuid}
                          onClick={() => toggleUserFavoriteSelection(user.uuid, user)}
                          className={`flex flex-1 w-full flex-col bg-white rounded-xl border border-gray-300 px-4 pt-4  ${isSelected ? 'ring-2 ring-purple-500 shadow-md ' : ' shadow-md '} `}
                          // className={`relative rounded-3xl overflow-hidden cursor-pointer transition-all bg-white ${isSelected ? 'ring-2 ring-purple-500 shadow-lg' : 'hover:shadow-md'}`}
                          style={{
                            minWidth: '11rem'
                            // height: '260px',
                            // width: '100%',
                            // boxShadow: '0px 2px 4px -1px rgba(110, 133, 166, 0.25), 0px 2px 2px rgba(110, 133, 166, 0.1)',
                          }}>
                          <div
                            className="flex flex-1 flex-col h-full justify-between"
                          // className="relative bg-gradient-to-br"
                          // style={{
                          //   padding: '12px 12px 0px',
                          //   height: '200px',
                          //   borderRadius: '10px 10px 0px 0px',
                          // }}
                          >
                            {/* imgge */}
                            <div className="relative w-full h-full" style={{ borderRadius: '0.5rem', aspectRatio: 4 / 5 }}>
                              {user.subscription_status === 'active' && (user.subscription_tier === 'premium'
                                || user.subscription_tier === 'pro') && (
                                  <div className="absolute top-2 left-2 bg-black text-white text-xs font-semibold px-2 py-1 rounded-full z-[1]">
                                    Pro
                                  </div>
                                )}
                              <Image
                                src={user.croppedAvatarUrl}
                                fallbackSrc={defaultAvatar}
                                alt={`${user.firstName} ${user.lastName}`}
                                className="w-full h-full object-cover rounded-lg"
                              />
                              {isSelected && (
                                <div className="absolute top-2 right-2 bg-purple-600 rounded-full p-1.5 z-[1]">
                                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            {/* name and other icon  */}
                            <div
                              className="flex items-center bg-white"
                              style={{
                                padding: '1rem 12px',
                                width: '100%',
                                height: '51px',
                              }}
                            >
                              <p
                                className="flex items-center text-left flex-1"
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',

                                  fontWeight: 600,
                                  fontSize: '1rem',
                                  lineHeight: '120%',
                                  letterSpacing: '-0.25px',
                                  color: '#0D1621',
                                  margin: 0,
                                  marginRight: '0.25rem'
                                }}
                                title={`${user.firstName} ${user.lastName}`}
                              >
                                {(() => {
                                  const fullName = `${user.firstName} ${user.lastName || ''}`;
                                  const shortName = user.lastName ? `${user.firstName} ${user.lastName.charAt(0)}` : user.firstName;
                                  return fullName.length * 9 > 140 ? shortName : fullName;
                                })()}
                              </p>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleUserFavoriteSelection(user.uuid, user);
                                }}
                                className="flex-shrink-0"
                              >
                                {favoritesUsers.findIndex((item) => item.uuid == user.uuid) >= 0 ? (
                                  <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
                                    <path
                                      d="M5.66669 11.9167C5.6667 10.9893 5.94801 10.0838 6.47346 9.31973C6.99891 8.55563 7.74378 7.96889 8.60969 7.63701C9.4756 7.30513 10.4218 7.24372 11.3234 7.46088C12.2249 7.67805 13.0394 8.16358 13.6592 8.85335C13.7028 8.90002 13.7556 8.93724 13.8143 8.96268C13.8729 8.98812 13.9361 9.00125 14 9.00125C14.0639 9.00125 14.1272 8.98812 14.1858 8.96268C14.2444 8.93724 14.2972 8.90002 14.3409 8.85335C14.9587 8.1591 15.7734 7.66949 16.6764 7.44968C17.5794 7.22988 18.5279 7.2903 19.3958 7.62292C20.2636 7.95553 21.0095 8.54456 21.5343 9.3116C22.0591 10.0786 22.3378 10.9873 22.3334 11.9167C22.3334 13.825 21.0834 15.25 19.8334 16.5L15.2567 20.9275C15.1014 21.1059 14.91 21.2491 14.6951 21.3478C14.4802 21.4464 14.2467 21.4982 14.0103 21.4997C13.7738 21.5012 13.5397 21.4524 13.3236 21.3565C13.1075 21.2605 12.9142 21.1197 12.7567 20.9433L8.16669 16.5C6.91669 15.25 5.66669 13.8333 5.66669 11.9167Z"
                                      fill="#8F79F1"
                                      stroke="#8570E4"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                ) : (
                                  <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
                                    <g filter="url(#filter0_d_7788_1309)">
                                      <path
                                        d="M5.66666 11.9167C5.66667 10.9893 5.94798 10.0838 6.47343 9.31973C6.99888 8.55563 7.74375 7.96889 8.60966 7.63701C9.47557 7.30513 10.4218 7.24372 11.3233 7.46088C12.2249 7.67805 13.0393 8.16358 13.6592 8.85335C13.7028 8.90002 13.7556 8.93724 13.8142 8.96268C13.8728 8.98812 13.9361 9.00125 14 9.00125C14.0639 9.00125 14.1271 8.98812 14.1858 8.96268C14.2444 8.93724 14.2972 8.90002 14.3408 8.85335C14.9587 8.1591 15.7733 7.66949 16.6764 7.44968C17.5794 7.22988 18.5279 7.2903 19.3957 7.62292C20.2635 7.95553 21.0095 8.54456 21.5343 9.3116C22.059 10.0786 22.3378 10.9873 22.3333 11.9167C22.3333 13.825 21.0833 15.25 19.8333 16.5L15.2567 20.9275C15.1014 21.1059 14.9099 21.2491 14.695 21.3478C14.4801 21.4464 14.2467 21.4982 14.0102 21.4997C13.7738 21.5012 13.5397 21.4524 13.3236 21.3565C13.1074 21.2605 12.9142 21.1197 12.7567 20.9433L8.16666 16.5C6.91666 15.25 5.66666 13.8333 5.66666 11.9167Z"
                                        stroke="#374151"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </g>
                                    <defs>
                                      <filter id="filter0_d_7788_1309" x="-2" y="0" width="32" height="32" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                        <feMorphology radius="2" operator="erode" in="SourceAlpha" result="effect1_dropShadow_7788_1309" />
                                        <feOffset dy="2" />
                                        <feGaussianBlur stdDeviation="2" />
                                        <feComposite in2="hardAlpha" operator="out" />
                                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0" />
                                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_7788_1309" />
                                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_7788_1309" result="shape" />
                                      </filter>
                                    </defs>
                                  </svg>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div id='load_more_id' className="flex items-center flex-col py-4" ref={observerAddFavRef} >
                    {noMoreData.length > 0 && filteredUsers.length > 0 && (
                      <div className="font-bold text-xl">All Profiles Loaded</div>
                    )}
                    {noMoreData.length <= 0 && !isUserLoading && filteredUsers.length > 0 && (
                      <Button
                        id="profiles_search_loadmore_btn"
                        variant="secondary"
                        btn_size="small"
                        className="cursor-pointer"
                        onClick={() => {
                          loadMore();
                        }}
                      >
                        <div className="w-6 h-6  flex items-center justify-center">
                          <RefreshCcwIcon className="h-6 w-6" aria-hidden="true" />
                        </div>
                        Load More
                      </Button>
                    )}
                  </div>

                </>
              )}

              {!isLoadingFilteredUsers && filteredUsers.length === 0 && (
                <div className="text-center py-8 sm:py-12">
                  <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p className="text-gray-500 text-sm sm:text-base">No members found matching your filters</p>
                  <button
                    onClick={handleClearFilters}
                    className="mt-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 flex-shrink-0">
          {/* Info Section */}
          <div className="p-3 sm:p-4 border-b border-gray-200">
            <p className="text-xs sm:text-sm text-gray-600 text-center lg:text-left lg:pl-4">
              Items added here will be included in <strong>All My Favorites</strong>.
            </p>
          </div>

          {/* Actions Section */}
          {selectedUsersToFavorite.length > 0 && (
            <div className="p-3 sm:p-4 bg-white flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {selectedUsersToFavorite.length} member{selectedUsersToFavorite.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={handleAddMultipleFavorites}
                className="px-4 sm:px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors text-sm sm:text-base"
              >
                Add to Favorites
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      ;
  }
  return (
    <div className="w-full flex flex-col flex-1 bg-gray-50">
      {viewingCollection != null &&
        <CollectionDetail
          collection={viewingCollection}
          showSharedCollection={showSharedCollection}
          onBack={async (refreshGroups: boolean) => {
            if (refreshGroups) {
              await loadGroups();
            }
            window.history.back();
            setViewingCollection(null);
          }}
          onSave={(saved: Group) => {
            let _groups = groups;
            let groupIndex = _groups.findIndex((item) => item.id == saved.id);
            if (groupIndex >= 0) {
              _groups[groupIndex] = { ..._groups[groupIndex], ...saved };
              setGroups(_groups);
            }
          }}
          onShowShareModal={() => {
            handleOpenShareModal(viewingCollection);
          }}
          favoriteUsers={favoritesUsers}
          onToggleFavorite={toggleFavorite}
          refreshCollection={(loadFav:boolean, loadCollection:boolean) => {
            //syca_check
            if(loadFav) {
              loadFavorites();
            }
            if(loadCollection) {
            loadGroups();
            }
          }}
          currentUser={currentUser}
          allAvailableUsers={allAvailableUsers}
          measurementRanges={measurementRanges}
        />
      }
      {viewingCollection == null && <>
        <Breadcrumb
          pages={breadCrumps}
        ></Breadcrumb>
        <div className="w-full xl:max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8 ">
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
                  Member Directory
                </h1>

                {/* Info Icon - Only show when guide card is hidden */}
                {!showGuideCard && (
                  <div
                    id="member_directory_info_icon"
                    className="flex items-center justify-center w-4 h-8 rounded-full text-blue-600 hover:text-blue-800 cursor-pointer transition-colors duration-200 ml-1 mt-0.5 sm:mt-0"
                    onClick={() => {
                      setShowGuideCard(true);
                      saveStorageNotification('member_directory', 1);
                    }}
                    title="Show directory information"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="mt-1"
                      style={{ flex: 'none', order: 0, flexGrow: 0 }}
                    >
                      <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      <path d="M9 12V9M9 6H9.0075" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Mobile Filter Button - Only show on "All Members" tab */}
              {activeTab === 'all' && (
                <button
                  onClick={() => {
                    setIsMobileFilterOpen(true);
                  }}
                  className="lg:hidden flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  Filters
                </button>
              )}
            </div>
            <style>{`
            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            .modal-slide-up {
              animation: slideUp 0.3s ease-out;
            }
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
              }
              to {
                transform: translateX(0);
              }
            }
            .slide-in-right {
              animation: slideInRight 0.3s ease-out;
            }
          `}</style>

            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-4 sm:space-x-8">
                <button
                  onClick={() => {
                    setOpenModals({});
                    window.history.pushState({}, '', `/users/directory?tab=all`);
                    setActiveTab('all')
                  }}
                  className={`${activeTab === 'all'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm`}
                >
                  All Members
                </button>
                <button
                  onClick={() => {
                    setOpenModals({});
                    window.history.pushState({}, '', `/users/directory?tab=favorites`);
                    setActiveTab('favorites')
                  }}
                  className={`${activeTab === 'favorites'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm`}
                >
                  Favorites
                </button>
                <button
                  onClick={() => {
                    setOpenModals({});
                    window.history.pushState({}, '', `/users/directory?tab=collections`);
                    setActiveTab('collections')
                  }}
                  className={`${activeTab === 'collections'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm`}
                >
                  Collections
                </button>
              </nav>
            </div>
          </div>

          {showGuideCard && showGuideUi()}

          {/* Mobile Filter Drawer */}
          {addMembersUserFilterMobile()}
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            {/* Desktop Filter Sidebar - Hidden on mobile transition-all duration-300 ease-in-out */}
            {addMembersUserFilterDesktop()}
            {/* transition-all duration-300 ease-in-out */}
            <div className="flex-1 ">
              {activeTab === 'all' ? (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-3 gap-3 sm:gap-0 border-b border-gray-200 pb-2.5">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">All Members</h2>
                    <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto justify-end sm:justify-start">
                      <span className="text-sm sm:text-base font-medium text-gray-500">Showing {filteredUsers.length.toLocaleString()} Members</span>
                      <div className="h-8 w-px bg-gray-300 hidden sm:block" style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}></div>
                      <div className="relative">
                        <button
                          data-sort-button
                          onClick={() => setShowSortMenu(!showSortMenu)}
                          className="p-2 hover:bg-gray-100 rounded-lg border border-gray-300"
                        >
                          <ArrowDownNarrowWide className="w-5 h-5 text-gray-600" />
                        </button>

                        {showSortMenu && (
                          <div
                            data-sort-menu
                            className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                          >
                            <button
                              onClick={() => {
                                setSortOption('favorites');
                                setShowSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'favorites' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              My Favorites First
                            </button>
                            <button
                              onClick={() => {
                                setSortOption('newest');
                                setShowSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'newest' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              New Members First
                            </button>
                            <button
                              onClick={() => {
                                setSortOption('oldest');
                                setShowSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'oldest' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              Old Members First
                            </button>
                            <button
                              onClick={() => {
                                setSortOption('name-asc');
                                setShowSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'name-asc' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              Name (A-Z)
                            </button>
                            <button
                              onClick={() => {
                                setSortOption('name-desc');
                                setShowSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${sortOption === 'name-desc' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              Name (Z-A)
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-2.5 justify-items-center items-start">
                    {filteredUsers.map((user) => (
                      <div
                        key={user.uuid}
                        className="overflow-hidden relative w-full flex flex-col bg-white rounded-xl border border-gray-300 shadow-md"
                        style={{
                          padding: '0px',
                          boxShadow: '0px 2px 4px -1px rgba(110, 133, 166, 0.25), 0px 2px 2px rgba(110, 133, 166, 0.1)',
                        }}
                      >
                        {/* Image Click Area */}
                        <div
                          className="bg-white relative w-full cursor-pointer"
                          style={{
                            padding: '15px 15px 0px',
                            flexGrow: 0,
                          }}
                          onClick={() => setprofileModalUser(user)}
                        >
                          <div
                            className="relative bg-gradient-to-br  w-full"
                            style={{
                              aspectRatio: '4/5',
                              borderRadius: '0.5rem',
                              overflow: 'hidden'
                            }}
                          >
                            <Image
                              className="inset-0 w-full h-full object-cover rounded-lg"
                              src={user.croppedAvatarUrl}
                              fallbackSrc={defaultAvatar}
                              alt={`${user.firstName} ${user.lastName}`}
                            />

                            {user.subscription_status === 'active' && (user.subscription_tier === 'premium'
                              || user.subscription_tier === 'pro') && (
                                <div className="absolute top-2 left-2 bg-black text-white text-xs font-semibold px-2 py-1 rounded-full z-[1]">
                                  Pro
                                </div>
                              )}
                          </div>
                        </div>

                        {/* Name and Action Container - Decoupled */}
                        <div
                          className="flex items-center bg-white"
                          style={{
                            padding: '1rem 12px',
                            width: '100%',
                            height: '51px',
                            flexGrow: 0,
                          }}
                        >
                          <a
                            href={`/users/${user.uuid}?from=directory`}
                            className="relative flex items-center text-left flex-1"
                            style={{ minWidth: 0 }}
                          >
                            <p
                              className="flex items-center text-left"
                              style={{
                                maxWidth: '140px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',

                                fontWeight: 600,
                                fontSize: '1rem',
                                lineHeight: '120%',
                                letterSpacing: '-0.25px',
                                color: '#0D1621',
                                margin: 0,
                              }}
                            >
                              {user.firstName} {user.lastName || ''}
                            </p>
                            <div className="absolute top-0 bottom-0 right-0 w-12 bg-gradient-to-l from-white pointer-events-none"></div>
                          </a>

                          <div className="flex items-center gap-2 ml-auto">
                            {/* Favorite toggle (heart) */}
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleFavorite(user.uuid, e, user); }}
                              className="flex items-center justify-center transition-all hover:scale-105"
                              style={{
                                width: '28px',
                                height: '28px',
                                padding: 0,
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                zIndex: 10
                              }}
                              aria-label={favoritesUsers.findIndex((item) => item.uuid == user.uuid) >= 0 ? 'Remove from favorites' : 'Add to favorites'}
                            >
                              {favoritesUsers.findIndex((item) => item.uuid == user.uuid) >= 0 ? (
                                // filled heart
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M5.66669 11.9167C5.6667 10.9893 5.94801 10.0838 6.47346 9.31973C6.99891 8.55563 7.74378 7.96889 8.60969 7.63701C9.4756 7.30513 10.4218 7.24372 11.3234 7.46088C12.2249 7.67805 13.0394 8.16358 13.6592 8.85335C13.7028 8.90002 13.7556 8.93724 13.8143 8.96268C13.8729 8.98812 13.9361 9.00125 14 9.00125C14.0639 9.00125 14.1272 8.98812 14.1858 8.96268C14.2444 8.93724 14.2972 8.90002 14.3409 8.85335C14.9587 8.1591 15.7734 7.66949 16.6764 7.44968C17.5794 7.22988 18.5279 7.2903 19.3958 7.62292C20.2636 7.95553 21.0095 8.54456 21.5343 9.3116C22.0591 10.0786 22.3378 10.9873 22.3334 11.9167C22.3334 13.825 21.0834 15.25 19.8334 16.5L15.2567 20.9275C15.1014 21.1059 14.91 21.2491 14.6951 21.3478C14.4802 21.4464 14.2467 21.4982 14.0103 21.4997C13.7738 21.5012 13.5397 21.4524 13.3236 21.3565C13.1075 21.2605 12.9142 21.1197 12.7567 20.9433L8.16669 16.5C6.91669 15.25 5.66669 13.8333 5.66669 11.9167Z"
                                    fill="#8F79F1"
                                    stroke="#8570E4"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              ) : (
                                // outline heart
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M5.66666 11.9167C5.66667 10.9893 5.94798 10.0838 6.47343 9.31973C6.99888 8.55563 7.74375 7.96889 8.60966 7.63701C9.47557 7.30513 10.4218 7.24372 11.3233 7.46088C12.2249 7.67805 13.0393 8.16358 13.6592 8.85335C13.7028 8.90002 13.7556 8.93724 13.8142 8.96268C13.8728 8.98812 13.9361 9.00125 14 9.00125C14.0639 9.00125 14.1271 8.98812 14.1858 8.96268C14.2444 8.93724 14.2972 8.90002 14.3408 8.85335C14.9587 8.1591 15.7733 7.66949 16.6764 7.44968C17.5794 7.22988 18.5279 7.2903 19.3957 7.62292C20.2635 7.95553 21.0095 8.54456 21.5343 9.3116C22.059 10.0786 22.3378 10.9873 22.3333 11.9167C22.3333 13.825 21.0833 15.25 19.8333 16.5L15.2567 20.9275C15.1014 21.1059 14.9099 21.2491 14.6950 21.3478C14.4801 21.4464 14.2467 21.4982 14.0102 21.4997C13.7738 21.5012 13.5397 21.4524 13.3236 21.3565C13.1074 21.2605 12.9142 21.1197 12.7567 20.9433L8.16666 16.5C6.91666 15.25 5.66666 13.8333 5.66666 11.9167Z"
                                    stroke="#374151"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </button>

                            {/* collection / add-to-group icon */}
                            {favoritesUsers.findIndex((item) => item.uuid == user.uuid) >= 0 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleGroupIconClick(user.uuid, e); }}
                                className="flex items-center justify-center transition-all hover:scale-105"
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  padding: 0,
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  zIndex: 10
                                }}
                                aria-label="Add to collection"
                              >
                                {isInGroup(user.uuid) ? (
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M15.8334 17.5L10 14.1667L4.16669 17.5V4.16667C4.16669 3.72464 4.34228 3.30072 4.65484 2.98816C4.9674 2.67559 5.39133 2.5 5.83335 2.5H14.1667C14.6087 2.5 15.0326 2.67559 15.3452 2.98816C15.6578 3.30072 15.8334 3.72464 15.8334 4.16667V17.5Z"
                                      fill="#8F79F1"
                                      stroke="#8570E4"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                ) : (
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M15.8334 17.5L10 14.1667L4.16669 17.5V4.16667C4.16669 3.72464 4.34228 3.30072 4.65484 2.98816C4.9674 2.67559 5.39133 2.5 5.83335 2.5H14.1667C14.6087 2.5 15.0326 2.67559 15.3452 2.98816C15.6578 3.30072 15.8334 3.72464 15.8334 4.16667V17.5Z"
                                      stroke="#374151"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                        </div>

                        {showGroupMenu === user.uuid && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className="absolute z-50 w-64 bg-white rounded-lg shadow-lg border border-gray-200"
                            style={{ top: '100%', right: 0, marginTop: '0.5rem' }}
                          >
                            <GroupContextMenu
                              user={user}
                              groups={groups}
                              onAddToGroup={(groupId) => handleAddToGroup(user.uuid, groupId)}
                              onOpenCreateModal={() => openCreateModal(user.uuid)}
                              onClose={() => setShowGroupMenu(null)}
                              isCreating={isCreatingGroup}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div id='load_more_id' className="flex items-center flex-col py-4" ref={observerRef} >
                    {noMoreData.length > 0 && filteredUsers.length > 0 && (
                      <div className="font-bold text-xl">All Profiles Loaded</div>
                    )}
                    {noMoreData.length <= 0 && !isUserLoading && filteredUsers.length > 0 && (
                      <Button
                        id="profiles_search_loadmore_btn"
                        variant="secondary"
                        btn_size="small"
                        className="cursor-pointer"
                        onClick={() => {
                          loadMore();
                        }}
                      >
                        <div className="w-6 h-6  flex items-center justify-center">
                          <RefreshCcwIcon className="h-6 w-6" aria-hidden="true" />
                        </div>
                        Load More
                      </Button>
                    )}
                  </div>
                </>
              ) : activeTab === 'favorites' ? (
                // Favorites Tab - Members View Only
                <>
                  {/* Header for Favorites Members View */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 px-4 sm:px-0"
                    style={{
                      borderBottom: '1px solid #E4E7EB',
                      padding: '1rem 0',
                      minHeight: '58px'
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3 sm:mb-0">
                      <h2 style={{
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: 'text-2xl',
                        lineHeight: '150%',
                        color: '#111827',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        All My Favorites
                        <span style={{ fontWeight: 400, marginLeft: '0.625rem' }}>({favoritesUsers.length})</span>
                      </h2>
                    </div>

                    <div className="flex items-center  gap-2 sm:gap-0 justify-between sm:justify-end w-full sm:w-auto">
                      {/* Add More Button */}
                      <button
                        onClick={() => {
                          setShowAddFavoritesModal(true);
                          setSelectedUsersToFavorite([]);
                          setAddFavoritesSearchTerm('');
                        }}
                        className="flex items-center justify-center bg-white rounded-md transition-colors hover:bg-gray-50 flex-1 sm:flex-none"
                        style={{
                          height: '36px',
                          padding: '8px 12px',
                          gap: '0.5rem',
                          border: '1px solid #D1D5DB',
                          minWidth: 'auto'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                          <path d="M9 3.75v10.5M3.75 9h10.5" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="hidden sm:inline" style={{
                          fontFamily: 'Poppins',
                          fontStyle: 'normal',
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: '21px',
                          color: '#374151',
                        }}>
                          Add More Favorites
                        </span>
                      </button>
                      <div className="h-8 w-px bg-gray-300 hidden sm:block" style={{ marginLeft: '1rem', marginRight: '1rem' }}></div>
                      {/* Sort Dropdown for Members */}
                      <div className="relative">
                        <button
                          data-favorites-sort-button
                          onClick={() => setShowFavoritesSortMenu(!showFavoritesSortMenu)}
                          className="p-2 border border-gray-300  hover:bg-gray-100 rounded-lg flex items-center justify-center"
                          style={{ width: '2.25rem', height: '2.25rem' }}
                          title="Sort members"
                        >
                          <ArrowDownNarrowWide className="w-5 h-5 text-gray-600" />
                        </button>

                        {showFavoritesSortMenu && (
                          <div
                            data-sort-menu
                            className="absolute right-0 mt-2 w-48 sm:w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                          >
                            <button
                              onClick={() => {
                                setFavoritesSortOption('newest');
                                setShowFavoritesSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${favoritesSortOption === 'newest' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              New Members First
                            </button>
                            <button
                              onClick={() => {
                                setFavoritesSortOption('oldest');
                                setShowFavoritesSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${favoritesSortOption === 'oldest' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              Old Members First
                            </button>
                            <button
                              onClick={() => {
                                setFavoritesSortOption('name-asc');
                                setShowFavoritesSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${favoritesSortOption === 'name-asc' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              Name (A-Z)
                            </button>
                            <button
                              onClick={() => {
                                setFavoritesSortOption('name-desc');
                                setShowFavoritesSortMenu(false);
                              }}
                              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${favoritesSortOption === 'name-desc' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                }`}
                            >
                              Name (Z-A)
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Favorites Members Grid */}
                  <div className="px-2 sm:px-0">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 gap-3 sm:gap-4 justify-items-center items-start">
                      {paginatedFavoritedUsers.map(user => (
                        <div
                          key={user.uuid}
                          className="overflow-hidden relative w-full flex flex-col bg-white rounded-xl border border-gray-300 shadow-md"
                          style={{
                            padding: '0px',
                            boxShadow: '0px 2px 4px -1px rgba(110, 133, 166, 0.25), 0px 2px 2px rgba(110, 133, 166, 0.1)',
                          }}
                        >
                          {/* Image Click Area */}
                          <div
                            className="bg-white relative w-full cursor-pointer"
                            style={{
                              padding: '15px 15px 0px',
                              flexGrow: 0,
                            }}
                            onClick={() => setprofileModalUser(user)}
                          >
                            <div
                              className="relative bg-gradient-to-br  w-full"
                              style={{
                                aspectRatio: '4/5',
                                borderRadius: '0.5rem',
                                overflow: 'hidden'
                              }}
                            >
                              <Image
                                className="inset-0 w-full h-full object-cover rounded-lg"
                                src={user.croppedAvatarUrl}
                                fallbackSrc={defaultAvatar}
                                alt={`${user.firstName} ${user.lastName}`}
                              />

                              {user.subscription_status === 'active' && (user.subscription_tier === 'premium'
                                || user.subscription_tier === 'pro') && (
                                  <div className="absolute top-2 left-2 bg-black text-white text-xs font-semibold px-2 py-1 rounded-full z-[1]">
                                    Pro
                                  </div>
                                )}
                            </div>
                          </div>

                          {/* Name and Action Container - Decoupled */}
                          <div
                            className="flex items-center bg-white"
                            style={{
                              padding: '1rem 12px',
                              width: '100%',
                              height: '51px',
                              flexGrow: 0,
                            }}
                          >
                            <a
                              href={`/users/${user.uuid}?from=directory`}
                              className="relative flex items-center text-left flex-1"
                              style={{ minWidth: 0 }}
                            >
                              <p
                                className="flex items-center text-left"
                                style={{
                                  maxWidth: '140px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',

                                  fontWeight: 600,
                                  fontSize: '1rem',
                                  lineHeight: '120%',
                                  letterSpacing: '-0.25px',
                                  color: '#0D1621',
                                  margin: 0,
                                }}
                              >
                                {user.firstName} {user.lastName || ''}
                              </p>
                              <div className="absolute top-0 bottom-0 right-0 w-12 bg-gradient-to-l from-white pointer-events-none"></div>
                            </a>

                            <div className="flex items-center gap-2 ml-auto">
                              {/* Favorite toggle (heart) */}
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleFavorite(user.uuid, e, user); }}
                                className="flex items-center justify-center transition-all hover:scale-105"
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  padding: 0,
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  zIndex: 10
                                }}
                                aria-label={favoritesUsers.findIndex((item) => item.uuid == user.uuid) >= 0 ? 'Remove from favorites' : 'Add to favorites'}
                              >
                                {favoritesUsers.findIndex((item) => item.uuid == user.uuid) >= 0 ? (
                                  // filled heart
                                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M5.66669 11.9167C5.6667 10.9893 5.94801 10.0838 6.47346 9.31973C6.99891 8.55563 7.74378 7.96889 8.60969 7.63701C9.4756 7.30513 10.4218 7.24372 11.3234 7.46088C12.2249 7.67805 13.0394 8.16358 13.6592 8.85335C13.7028 8.90002 13.7556 8.93724 13.8143 8.96268C13.8729 8.98812 13.9361 9.00125 14 9.00125C14.0639 9.00125 14.1272 8.98812 14.1858 8.96268C14.2444 8.93724 14.2972 8.90002 14.3409 8.85335C14.9587 8.1591 15.7734 7.66949 16.6764 7.44968C17.5794 7.22988 18.5279 7.2903 19.3958 7.62292C20.2636 7.95553 21.0095 8.54456 21.5343 9.3116C22.0591 10.0786 22.3378 10.9873 22.3334 11.9167C22.3334 13.825 21.0834 15.25 19.8334 16.5L15.2567 20.9275C15.1014 21.1059 14.91 21.2491 14.6951 21.3478C14.4802 21.4464 14.2467 21.4982 14.0103 21.4997C13.7738 21.5012 13.5397 21.4524 13.3236 21.3565C13.1075 21.2605 12.9142 21.1197 12.7567 20.9433L8.16669 16.5C6.91669 15.25 5.66669 13.8333 5.66669 11.9167Z"
                                      fill="#8F79F1"
                                      stroke="#8570E4"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                ) : (
                                  // outline heart
                                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M5.66666 11.9167C5.66667 10.9893 5.94798 10.0838 6.47343 9.31973C6.99888 8.55563 7.74375 7.96889 8.60966 7.63701C9.47557 7.30513 10.4218 7.24372 11.3233 7.46088C12.2249 7.67805 13.0393 8.16358 13.6592 8.85335C13.7028 8.90002 13.7556 8.93724 13.8142 8.96268C13.8728 8.98812 13.9361 9.00125 14 9.00125C14.0639 9.00125 14.1271 8.98812 14.1858 8.96268C14.2444 8.93724 14.2972 8.90002 14.3408 8.85335C14.9587 8.1591 15.7733 7.66949 16.6764 7.44968C17.5794 7.22988 18.5279 7.2903 19.3957 7.62292C20.2635 7.95553 21.0095 8.54456 21.5343 9.3116C22.059 10.0786 22.3378 10.9873 22.3333 11.9167C22.3333 13.825 21.0833 15.25 19.8333 16.5L15.2567 20.9275C15.1014 21.1059 14.9099 21.2491 14.6950 21.3478C14.4801 21.4464 14.2467 21.4982 14.0102 21.4997C13.7738 21.5012 13.5397 21.4524 13.3236 21.3565C13.1074 21.2605 12.9142 21.1197 12.7567 20.9433L8.16666 16.5C6.91666 15.25 5.66666 13.8333 5.66666 11.9167Z"
                                      stroke="#374151"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </button>

                              {/* collection / add-to-group icon */}
                              {favoritesUsers.findIndex((item) => item.uuid == user.uuid) >= 0 && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleGroupIconClick(user.uuid, e); }}
                                  className="flex items-center justify-center transition-all hover:scale-105"
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    padding: 0,
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    zIndex: 10
                                  }}
                                  aria-label="Add to collection"
                                >
                                  {isInGroup(user.uuid) ? (
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path
                                        d="M15.8334 17.5L10 14.1667L4.16669 17.5V4.16667C4.16669 3.72464 4.34228 3.30072 4.65484 2.98816C4.9674 2.67559 5.39133 2.5 5.83335 2.5H14.1667C14.6087 2.5 15.0326 2.67559 15.3452 2.98816C15.6578 3.30072 15.8334 3.72464 15.8334 4.16667V17.5Z"
                                        fill="#8F79F1"
                                        stroke="#8570E4"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  ) : (
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path
                                        d="M15.8334 17.5L10 14.1667L4.16669 17.5V4.16667C4.16669 3.72464 4.34228 3.30072 4.65484 2.98816C4.9674 2.67559 5.39133 2.5 5.83335 2.5H14.1667C14.6087 2.5 15.0326 2.67559 15.3452 2.98816C15.6578 3.30072 15.8334 3.72464 15.8334 4.16667V17.5Z"
                                        stroke="#374151"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>

                          {showGroupMenu === user.uuid && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="absolute z-50 w-64 bg-white rounded-lg shadow-lg border border-gray-200"
                              style={{ top: '100%', right: 0, marginTop: '0.5rem' }}
                            >
                              <GroupContextMenu
                                user={user}
                                groups={groups}
                                onAddToGroup={(groupId) => handleAddToGroup(user.uuid, groupId)}
                                onOpenCreateModal={() => openCreateModal(user.uuid)}
                                onClose={() => setShowGroupMenu(null)}
                                isCreating={isCreatingGroup}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Lazy loading observer for favorites */}
                    {paginatedFavoritedUsers.length > 0 && paginatedFavoritedUsers.length < sortedFavoritedUsers.length && (
                      <div className="flex items-center justify-center py-4 col-span-full" ref={observerRef}>
                        <div className="w-6 h-6 flex items-center justify-center text-gray-500">
                          <RefreshCcwIcon className="h-6 w-6 animate-spin" aria-hidden="true" />
                        </div>
                      </div>
                    )}

                    {/* Empty State for Members */}
                    {paginatedFavoritedUsers.length === 0 && (
                      <div className="text-center py-12 sm:py-16 px-4 col-span-full">
                        <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No favorite members</h3>
                        <p className="text-sm sm:text-base text-gray-500">Add some members to your favorites to see them here</p>
                        <button
                          onClick={() => setShowAddFavoritesModal(true)}
                          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        >
                          Add Favorites
                        </button>
                      </div>
                    )}

                    {/* Pagination for favorites members - Mobile Responsive */}
                    {/* {favoritesTotalPages > 1 && (
                    <div className="mt-6 sm:mt-8 flex items-center justify-center gap-1 sm:gap-2 px-2 col-span-full">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        className="p-1 sm:p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={currentPage === 1}
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>

                      {favoritesPageNumbers.map((page, index) => (
                        page === '...' ? (
                          <span key={`ellipsis-fav-${index}`} className="px-1 sm:px-2 text-sm">...</span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page as number)}
                            className={`px-2 sm:px-3 py-1 rounded font-medium text-sm ${currentPage === page
                              ? 'bg-blue-500 text-white'
                              : 'hover:bg-gray-100 text-gray-700'
                              }`}
                          >
                            {page}
                          </button>
                        )
                      ))}

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        className="p-1 sm:p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={currentPage === favoritesTotalPages}
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )} */}
                  </div>
                </>
              ) : (
                // Collections Tab
                <>
                  {/* Header for Collections */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 px-4 sm:px-0"
                    style={{
                      borderBottom: '1px solid #E4E7EB',
                      padding: '1rem 0',
                      minHeight: '58px'
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3 sm:mb-0">
                      <h2 style={{
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: '20px',
                        lineHeight: '150%',
                        color: '#111827',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        My Collections
                      </h2>
                    </div>

                    <div className="flex items-center gap-2 justify-between sm:justify-end w-full sm:w-auto">
                      {/* Collection Filter */}
                      <div className="flex items-center gap-2 flex-1 sm:flex-none">
                        <span className="hidden sm:inline" style={{
                          fontFamily: 'Poppins',
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '21px',
                          color: '#374151',
                          whiteSpace: 'nowrap'
                        }}>
                          Show:
                        </span>
                        <div className="relative flex-1 sm:flex-none">
                          <button
                            data-collection-button
                            onClick={() => setShowCollectionFilter(!showCollectionFilter)}
                            className="flex items-center justify-between w-full sm:w-48"
                            style={{
                              height: '38px',
                              padding: '9px 12px',
                              background: '#FFFFFF',
                              border: '1px solid #D1D5DB',
                              boxShadow: '0px 2px 4px -2px rgba(0, 0, 0, 0.12)',
                              borderRadius: '6px'
                            }}
                          >
                            <span style={{
                              fontFamily: 'Poppins',
                              fontWeight: 500,
                              fontSize: '14px',
                              lineHeight: '21px',
                              color: '#374151',
                              textAlign: 'left',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {collectionFilter === 'all' && `All Collections`}
                              {collectionFilter === 'active' && `Active`}
                              {collectionFilter === 'archived' && `Archived`}
                            </span>
                            <div className="flex items-center gap-1">
                              <span style={{
                                fontFamily: 'Poppins',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '18px',
                                color: '#374151'
                              }}>
                                ({collectionFilter === 'all' ? groupCounts.total + 1 : collectionFilter === 'active' ? groupCounts.active + 1 : groupCounts.archived})
                              </span>
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          </button>

                          {showCollectionFilter && (
                            <div
                              data-collection-filter
                              className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-full sm:w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                            >
                              <button
                                onClick={() => {
                                  setCollectionFilter('all');
                                  setShowCollectionFilter(false);
                                }}
                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${collectionFilter === 'all' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                  }`}
                              >
                                <span>All Collections ({groupCounts.total + 1})</span>
                                {collectionFilter === 'all' && (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setCollectionFilter('active');
                                  setShowCollectionFilter(false);
                                }}
                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${collectionFilter === 'active' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                  }`}
                              >
                                <span>Active ({groupCounts.active + 1})</span>
                                {collectionFilter === 'active' && (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  setCollectionFilter('archived');
                                  setShowCollectionFilter(false);
                                }}
                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${collectionFilter === 'archived' ? 'text-blue-600 font-medium' : 'text-gray-700'
                                  }`}
                              >
                                <span>Archived ({groupCounts.archived})</span>
                                {collectionFilter === 'archived' && (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Collections Grid */}
                  <div className="px-2 sm:px-0 flex flex-col items-center w-full">
                    <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 justify-items-stretch xs:justify-items-center items-start w-full sm:w-auto">
                      {/* New Collection Card */}
                      {collectionFilter !== 'archived' && (
                        <div
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openCreateModal();
                          }}
                          className="relative bg-white border border-gray-300 rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:border-blue-400 transition-all duration-200 cursor-pointer"
                          style={{
                            width: isMobile ? '100%' : '18.125rem',
                            maxWidth: isMobile ? '100%' : '18.125rem',
                            boxShadow: '0px 2px 4px -1px rgba(110, 133, 166, 0.25), 0px 2px 2px rgba(110, 133, 166, 0.1)',
                            borderRadius: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignSelf: 'start',
                          }}
                        >
                          <div
                            className="flex justify-center items-center w-full"
                            style={{
                              padding: '1rem 1rem 0px',
                              height: '17.125rem',
                              background: '#FFFFFF',
                              borderRadius: '10px 10px 0px 0px',
                              pointerEvents: 'none',
                              flexShrink: 0
                            }}
                          >
                            <div
                              className="flex items-center justify-center w-full h-full"
                              style={{
                                background: '#E3E6EB',
                                border: '1.5px dashed #9CA3AF',
                                borderRadius: '0.5rem',
                                pointerEvents: 'none'
                              }}
                            >
                              <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
                                <path d="M24 10v28M10 24h28" stroke="#6B7280" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          </div>

                          <div
                            className="flex items-center justify-center w-full"
                            style={{
                              padding: '1rem',
                              height: '60px',
                              pointerEvents: 'none'
                            }}
                          >
                            <span
                              style={{
                                fontFamily: 'Poppins',
                                fontWeight: 600,
                                fontSize: '1rem',
                                lineHeight: '120%',
                                letterSpacing: '-0.25px',
                                color: '#0D1621',
                                textAlign: 'center'
                              }}
                            >
                              New Collection
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Group Cards */}
                      {filteredGroups.map((group) => {
                        const groupAvatars = group.teamMemberAvatars || [];
                        const hasMembers = groupAvatars.length > 0;

                        const main = groupAvatars[0] || null;
                        const rightTop = groupAvatars[1] || null;
                        const rightBottom = groupAvatars[2] || null;
                        const isArchived = isGroupArchived(group);

                        // if you are not owner and group is archived then dont show it
                        if (!group.isOwner && isArchived) {
                          return <></>;
                        }
                        return (
                          <div
                            key={group.id}
                            ref={(el) => cardRefs.current[group.id] = el}
                            onClick={() => handleCollectionClick(group)}
                            className="relative bg-white border border-gray-300 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 h-full"
                            style={{
                              width: isMobile ? '100%' : '18.125rem',
                              // height: '334px',
                              boxShadow: '0px 2px 4px -1px rgba(110, 133, 166, 0.25), 0px 2px 2px rgba(110, 133, 166, 0.1)',
                              borderRadius: '12px',
                              opacity: isArchived ? 0.5 : 1,
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            {/* Images Section - Fixed Height */}
                            <div
                              onClick={() => {
                                if(group.isOwner) {
                                  setshowSharedCollection(true);
                                }
                                setViewingCollection(group);
                              }}
                              className="cursor-pointer flex flex-row gap-2"
                              style={{
                                padding: '1rem 1rem 0px',
                                height: '17.125rem',
                                // height: '274px',
                                // minHeight: '274px',
                                // maxHeight: '274px',
                                background: '#FFFFFF',
                                borderRadius: '10px 10px 0px 0px',
                                flexShrink: 0
                              }}
                            >
                              {/* <div 
                              className="flex gap-2" 
                              style={{ 
                                height: '258px',
                                minHeight: '258px',
                                maxHeight: '258px',
                                width: '100%'
                              }}
                            > */}
                              {/* Left large image - 50% width */}
                              <div
                                className='flex-1 flex'
                                style={{
                                  // width: '50%',
                                  // minWidth: '50%',
                                  // height: '100%',
                                  flexShrink: 0
                                }}
                              >
                                {main ? (
                                  <div
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      borderRadius: '6px',
                                      overflow: 'hidden',
                                      position: 'relative'
                                    }}
                                  >
                                    <img
                                      src={main}
                                      onError={(e) => {
                                        e.currentTarget.src = defaultAvatar;
                                      }}
                                      alt={group.title}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block'
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      background: '#F9FAFB',
                                      border: hasMembers ? 'none' : '1.5px dashed #9CA3AF',
                                      borderRadius: '6px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  />
                                )}
                              </div>

                              {/* Right column with 2 images - 50% width */}
                              <div
                                className="flex flex-col gap-2 h-full"
                                style={{
                                  width: '6.25rem',
                                  flexShrink: 0
                                }}
                              >
                                {/* Top right image - 50% height */}
                                <div
                                  style={{
                                    height: '7.8125rem',
                                    // height: '50%',
                                    // minHeight: 'calc(50% - 4px)',
                                    width: '100%',
                                    flexShrink: 0
                                  }}
                                >
                                  {rightTop ? (
                                    <div
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '6px',
                                        overflow: 'hidden',
                                        position: 'relative'
                                      }}
                                    >
                                      <img
                                        src={rightTop}
                                        onError={(e) => {
                                          e.currentTarget.src = defaultAvatar;
                                        }}
                                        alt={`${group.title}-1`}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          display: 'block'
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        background: '#F9FAFB',
                                        border: hasMembers ? 'none' : '1.5px dashed #9CA3AF',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    />
                                  )}
                                </div>

                                {/* Bottom right image - 50% height */}
                                <div
                                  style={{
                                    height: '7.8125rem',
                                    // height: '50%',
                                    // minHeight: 'calc(50% - 4px)',
                                    // width: '100%',
                                    flexShrink: 0
                                  }}
                                >
                                  {rightBottom ? (
                                    <div
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '6px',
                                        overflow: 'hidden',
                                        position: 'relative'
                                      }}
                                    >
                                      <img
                                        src={rightBottom}
                                        onError={(e) => {
                                          e.currentTarget.src = defaultAvatar;
                                        }}
                                        alt={`${group.title}-2`}
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          display: 'block'
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <div
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        background: '#F9FAFB',
                                        border: hasMembers ? 'none' : '1.5px dashed #9CA3AF',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                              {/* </div> */}
                            </div>

                            {/* Title and Action Buttons Section - Fixed Height */}
                            <div
                              style={{
                                padding: '1rem',
                                height: '60px',
                                minHeight: '60px',
                                maxHeight: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexShrink: 0
                              }}
                            >
                              <div onClick={() => {
                                if(group.isOwner) {
                                  setshowSharedCollection(true);
                                }
                                setViewingCollection(group);
                              }} className="flex items-center gap-1 flex-1 min-w-0 cursor-pointer">
                                {!isArchived ? (
                                  <svg width="16" height="28" viewBox="0 0 16 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clipPath="url(#clip0_8098_2599)">
                                      <path d="M14.5341 12.6667C14.8385 14.1609 14.6215 15.7143 13.9193 17.0679C13.2171 18.4214 12.072 19.4934 10.6751 20.1049C9.27816 20.7164 7.71382 20.8305 6.24293 20.4282C4.77205 20.026 3.48353 19.1316 2.59225 17.8943C1.70097 16.657 1.26081 15.1515 1.34518 13.6289C1.42954 12.1063 2.03332 10.6587 3.05583 9.52744C4.07835 8.39616 5.45779 7.64961 6.96411 7.4123C8.47043 7.17498 10.0126 7.46123 11.3334 8.22333M6 13.3333L8 15.3333L14.6667 8.66667" stroke="#2EA901" strokeLinecap="round" strokeLinejoin="round" /></g>
                                    <defs><clipPath id="clip0_8098_2599"><rect width="16" height="16" fill="white" transform="translate(0 6)" /></clipPath></defs>
                                  </svg>
                                ) : (
                                  <CircleOffIcon className="w-4 h-4 flex-shrink-0" />
                                  // <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 16 16" fill="none">
                                  //   <circle cx="8" cy="8" r="7" stroke="#374151" strokeWidth="1" />
                                  //   <path d="M5 5l6 6M11 5l-6 6" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
                                  // </svg>
                                )}
                                <h3
                                  className="truncate"
                                  style={{
                                    fontFamily: 'Poppins',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    lineHeight: '120%',
                                    letterSpacing: '-0.25px',
                                    color: isArchived ? '#576C89' : '#0D1621',
                                    marginLeft: '4px',
                                  }}
                                  title={group.title}
                                >
                                  {group.title}
                                </h3>
                              </div>

                              <div className="flex items-center flex-shrink-0" style={{ gap: '0px' }}>
                                {group.isOwner && <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if(group.archived) {
                                        return;
                                      }
                                      handleOpenShareModal(group);
                                    }}
                                    className="hover:bg-gray-50 rounded-md"
                                    title="Share Access"
                                    style={{
                                      width: '40px',
                                      height: '28px',
                                      padding: '0px 10px',
                                      borderRadius: '8px 0px 0px 8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      opacity: isArchived ? 0.4 : 1
                                    }}
                                  >
                                    <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                                      <path
                                        d="M2.67 8v3.33c0 .74.6 1.34 1.33 1.34h8c.74 0 1.33-.6 1.33-1.34V8M10.67 3.33L8 1.33 5.33 3.33M8 1.33v8"
                                        fillOpacity="0.7"
                                        stroke="#374151"
                                        strokeWidth="1"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleGroupInfoClick(group, group.id, e);
                                    }}
                                    className="hover:bg-gray-50 rounded-md"
                                    title="Info"
                                    style={{
                                      width: '36px',
                                      height: '28px',
                                      padding: '0px 10px',
                                      borderLeft: '1px solid #D1D5DB',
                                      borderRadius: '0px 8px 8px 0px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M9.33317 11.3333H3.33317M9.33317 11.3333C9.33317 12.4379 10.2286 13.3333 11.3332 13.3333C12.4377 13.3333 13.3332 12.4379 13.3332 11.3333C13.3332 10.2288 12.4377 9.33334 11.3332 9.33334C10.2286 9.33334 9.33317 10.2288 9.33317 11.3333ZM12.6665 4.66667H6.6665M6.6665 4.66667C6.6665 5.77124 5.77107 6.66667 4.6665 6.66667C3.56193 6.66667 2.6665 5.77124 2.6665 4.66667C2.6665 3.5621 3.56193 2.66667 4.6665 2.66667C5.77107 2.66667 6.6665 3.5621 6.6665 4.66667Z" stroke="#374151" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </button>
                                </>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Empty State for Collections */}
                    {filteredGroups.length === 0 && collectionFilter === 'archived' && (
                      <div className="text-center py-12 sm:py-16 px-4 w-full">
                        <CircleOffIcon className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-3 sm:mb-4" />
                        {/* <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300 mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg> */}
                        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No archived collections</h3>
                        <p className="text-sm sm:text-base text-gray-500">You don't have any archived collections yet</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {Object.entries(openModals).map(([groupId, { group, position }]) => {
          const groupMembers = currentMembers.filter(member =>
            manageMembersGroup?.id === group.id ? true : member.groups?.includes(group.id)
          );

          const memberAvatars = groupMembers.length > 0
            ? groupMembers.map(m => m.croppedAvatarUrl).filter(Boolean)
            : (group.teamMemberAvatars || []);

          // const hasMembers = memberAvatars.length > 0;
          const hasMembers = 0;

          let sharedUsers = [];
          if (group.shared_users && group.shared_users.length > 0) {
            sharedUsers = group.shared_users;
          }

          return (
            <>
              {/* Mobile: Full Screen Modal */}
              <div
                key={`${groupId}-mobile`}
                className="md:hidden fixed inset-0 bg-customGray z-50 flex items-center justify-center p-4"
                // style={{ backgroundColor: '#6b7a8a' }}
                onClick={() => handleCloseModal(parseInt(groupId))}
              >
                <div
                  data-collection-info-modal
                  data-modal-id={groupId}
                  className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto flex flex-col overflow-hidden modal-slide-up"
                  style={{
                    maxHeight: '90vh',
                    border: '1px solid #D1D5DB',
                    boxShadow: '0px 2px 4px -1px rgba(110, 133, 166, 0.25), 0px 2px 2px rgba(110, 133, 166, 0.1)',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Mobile Header */}
                  <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200" style={{ minHeight: '60px' }}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {!isGroupArchived(group) ? (
                        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="7" stroke="#2EA901" strokeWidth="1" />
                          <path d="M5 8l2 2 4-4" stroke="#2EA901" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <CircleOffIcon className="w-5 h-5 flex-shrink-0" />
                        // <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 16 16" fill="none">
                        //   <circle cx="8" cy="8" r="7" stroke="#9CA3AF" strokeWidth="1" />
                        //   <path d="M5 5l6 6M11 5l-6 6" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
                        // </svg>
                      )}
                      {editingTitleId !== null ? (
                        <input
                          autoFocus
                          value={editingTitleId}
                          onChange={(e) => {
                            console.log('onChange', e.target.value);
                            if (e.target.value) {
                              seteditingTitleId(e.target.value)
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSaveTitle(group.id, editingTitleId ?? '');
                            } else if (e.key === 'Escape') {
                              seteditingTitleId(null);
                            }
                          }}
                          onBlur={() => {
                            console.log('blue');
                            handleSaveTitle(group.id, editingTitleId);
                          }}
                          className="w-full text-left border border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '150%',
                            color: '#0D1621',
                            resize: 'vertical'
                          }}
                        />
                      ) : (
                        <h3
                          onDoubleClick={() => {
                            if (!isGroupArchived(group)) {
                              seteditingTitleId(group.title);
                            }
                          }}
                          className="truncate text-lg font-semibold"
                          style={{
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: '18px',
                            lineHeight: '120%',
                            letterSpacing: '-0.25px',
                            color: '#0D1621',
                          }}
                        >
                          {group.title}
                        </h3>
                      )}
                    </div>
                    <button
                      onClick={() => handleCloseModal(parseInt(groupId))}
                      className="flex-shrink-0 hover:bg-gray-100 rounded-full p-2"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 16 16" fill="none">
                        <path d="M4 4l8 8M12 4l-8 8" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>

                  {/* Mobile Content */}
                  <div className="flex-1 overflow-y-auto px-5 py-5">
                    {/* Description */}
                    <div className="mb-6">
                      {editingDescriptionId === group.id ? (
                        <textarea
                          autoFocus
                          value={descriptionValues[group.id] ?? group.description ?? ''}
                          onChange={(e) => setDescriptionValues(prev => ({ ...prev, [group.id]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSaveDescription(group.id, descriptionValues[group.id] ?? group.description ?? '');
                            } else if (e.key === 'Escape') {
                              setEditingDescriptionId(null);
                              setDescriptionValues(prev => {
                                const newValues = { ...prev };
                                delete newValues[group.id];
                                return newValues;
                              });
                            }
                          }}
                          onBlur={() => {
                            handleSaveDescription(group.id, descriptionValues[group.id] ?? group.description ?? '');
                          }}
                          className="w-full text-center border border-blue-500 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          style={{
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '150%',
                            color: '#0D1621',
                            minHeight: '120px',
                            resize: 'vertical'
                          }}
                          rows={4}
                        />
                      ) : (
                        <div
                          onDoubleClick={() => {
                            setEditingDescriptionId(group.id);
                            setDescriptionValues(prev => ({ ...prev, [group.id]: group.description || '' }));
                          }}
                          className="text-center cursor-pointer hover:bg-gray-50 rounded-lg p-3 transition-colors"
                          style={{
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '150%',
                            color: group.description ? '#0D1621' : '#9CA3AF',
                            minHeight: '120px'
                          }}
                          title="Double-click to edit"
                        >
                          {group.description || 'Double-click to add description...'}
                        </div>
                      )}
                    </div>

                    {/* Member Avatars */}
                    <div className="flex items-center justify-center gap-2 py-4">
                      <div className="flex items-center" style={{ height: '40px' }}>
                        {hasMembers ? (
                          <>
                            {memberAvatars.slice(0, 3).map((avatarUrl, index) => (
                              <div
                                key={index}
                                className="rounded-full border-2 border-white shadow-sm object-cover overflow-hidden"
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  marginLeft: index > 0 ? '-8px' : '0',
                                  zIndex: 3 - index
                                }}
                              >
                                <img
                                  src={avatarUrl || defaultAvatar}
                                  onError={(e) => {
                                    e.currentTarget.src = defaultAvatar;
                                  }}
                                  alt={`Member ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                            {memberAvatars.length > 3 && (
                              <div
                                className="rounded-full flex items-center justify-center"
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  marginLeft: '-8px',
                                  background: '#E0D4FF',
                                  border: '2px solid #9061E2',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: '#9061E2'
                                }}
                              >
                                +{memberAvatars.length - 3}
                              </div>
                            )}
                          </>
                        ) : (
                          <div
                            className="rounded-full flex items-center justify-center border-2 border-dashed border-gray-300"
                            style={{
                              width: '40px',
                              height: '40px',
                              background: '#F9FAFB'
                            }}
                          >
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Mobile Actions */}
                  <div className="border-t border-gray-200 p-4 bg-white space-y-3">
                    <button
                      // onClick={() => handleOpenManageMembers(group)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenShareModal(group);
                      }}

                      className="w-full py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      disabled={isGroupArchived(group)}
                      style={{
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#374151',
                      }}
                    >
                      Share Access
                    </button>
                    <button
                      onClick={() => isGroupArchived(group) ? handleUnarchive(group.id) : handleArchive(group.id)}
                      className="w-full py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      style={{
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: '14px',
                        color: isGroupArchived(group) ? '#2EA901' : '#374151',
                      }}
                    >
                      {isGroupArchived(group) ? 'Restore' : 'Archive'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Desktop: Positioned Modal */}
              <div
                key={`${groupId}-desktop`}
                data-collection-info-modal
                data-modal-id={groupId}
                className="hidden md:flex absolute bg-white rounded-xl shadow-lg z-50 flex-col"
                style={{
                  top: `${position.top}px`,
                  left: `${position.left}px`,
                  width: '290px',
                  minWidth: '290px',
                  height: '334px',
                  border: '1px solid #D1D5DB',
                  boxShadow: '0px 2px 4px -1px rgba(110, 133, 166, 0.25), 0px 2px 2px rgba(110, 133, 166, 0.1)',
                  borderRadius: '12px'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-4 py-4" style={{ height: '60px', flexShrink: 0 }}>
                  <div className="flex items-center gap-1 flex-1">
                    {!isGroupArchived(group) ? (
                      <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7" stroke="#2EA901" strokeWidth="1" />
                        <path d="M5 8l2 2 4-4" stroke="#2EA901" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <CircleOffIcon className="w-4 h-4 flex-shrink-0" />
                    )}

                    {editingTitleId !== null ? (
                      <input
                        autoFocus
                        value={editingTitleId}
                        onChange={(e) => {
                          console.log('onChange', e.target.value);
                          if (e.target.value || e.target.value == '') {
                            seteditingTitleId(e.target.value)
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSaveTitle(group.id, editingTitleId ?? '');
                          } else if (e.key === 'Escape') {
                            seteditingTitleId(null);
                          }
                        }}
                        onBlur={() => {
                          console.log('blue');
                          handleSaveTitle(group.id, editingTitleId);
                        }}
                        className="w-full text-left border border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '150%',
                          color: '#0D1621',
                          resize: 'vertical'
                        }}
                      />
                    ) : (
                      <h3
                        onDoubleClick={() => {
                          if (!isGroupArchived(group)) {
                            seteditingTitleId(group.title);
                          }
                        }}
                        className="truncate"
                        style={{
                          fontFamily: 'Poppins',
                          fontWeight: 600,
                          fontSize: '1rem',
                          lineHeight: '120%',
                          letterSpacing: '-0.25px',
                          color: '#0D1621',
                          marginLeft: '4px'
                        }}
                      >
                        {group.title}
                      </h3>
                    )}

                  </div>
                  <button
                    onClick={() => handleCloseModal(parseInt(groupId))}
                    className="flex-shrink-0 hover:bg-gray-100 rounded p-1"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
                      <path d="M4 4l8 8M12 4l-8 8" stroke="#374151" strokeWidth="1" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>

                <div className="flex flex-col items-center px-5 py-4" style={{ height: '274px', flexShrink: 0 }}>
                  {/* Description - Fixed Height */}
                  <div className="flex-1" style={{ maxHeight: '114px' }}>
                    {editingDescriptionId === group.id ? (
                      <textarea
                        autoFocus
                        value={descriptionValues[group.id] ?? group.description ?? ''}
                        onChange={(e) => setDescriptionValues(prev => ({ ...prev, [group.id]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSaveDescription(group.id, descriptionValues[group.id] ?? group.description ?? '');
                          } else if (e.key === 'Escape') {
                            setEditingDescriptionId(null);
                            setDescriptionValues(prev => {
                              const newValues = { ...prev };
                              delete newValues[group.id];
                              return newValues;
                            });
                          }
                        }}
                        onBlur={() => {
                          handleSaveDescription(group.id, descriptionValues[group.id] ?? group.description ?? '');
                        }}
                        className="w-full text-center border border-blue-500 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{
                          fontFamily: 'Poppins',
                          fontWeight: 400,
                          fontSize: '13px',
                          lineHeight: '150%',
                          color: '#0D1621',
                          maxHeight: '114px',
                          resize: 'none'
                        }}
                        rows={4}
                      />
                    ) : (
                      <div
                        onDoubleClick={() => {
                          setEditingDescriptionId(group.id);
                          setDescriptionValues(prev => ({ ...prev, [group.id]: group.description || '' }));
                        }}
                        className="text-center overflow-hidden cursor-pointer hover:bg-gray-50 rounded p-2 transition-colors"
                        style={{
                          fontFamily: 'Poppins',
                          fontWeight: 400,
                          fontSize: '13px',
                          lineHeight: '150%',
                          color: group.description ? '#0D1621' : '#9CA3AF',
                          display: '-webkit-box',
                          WebkitLineClamp: 6,
                          WebkitBoxOrient: 'vertical',
                          maxHeight: '114px'
                        }}
                        title="Double-click to edit"
                      >
                        {group.description || 'Double-click to add description...'}
                      </div>
                    )}
                  </div>

                  {/* Viewers Avatars - Fixed Height 40px */}
                  <div className="flex items-center justify-center gap-1" style={{ height: '40px', marginTop: '1rem', flexShrink: 0 }}>
                    <div className="flex items-center" style={{ height: '32px' }}>
                      {sharedUsers.length > 0 ? (
                        <>
                          {sharedUsers.slice(0, 2).map((sharedUser, index) => (
                            <div
                              key={index}
                              className="rounded-full border-2 border-white shadow-sm object-cover overflow-hidden"
                              style={{
                                width: '32px',
                                height: '32px',
                                marginLeft: index > 0 ? '-6px' : '0',
                                zIndex: 2 - index
                              }}
                            >
                              <img
                                src={sharedUser.croppedAvatarUrl || sharedUser.avatarUrl || defaultAvatar}
                                alt={`Member ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                          {sharedUsers.length > 2 && (
                            <div
                              className="rounded-full flex items-center justify-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                marginLeft: '-6px',
                                background: '#E0D4FF',
                                border: '1.5px solid #9061E2',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#9061E2'
                              }}
                            >
                              +{sharedUsers.length - 2}
                            </div>
                          )}
                        </>
                      ) : (
                        <div
                          className="rounded-full flex items-center justify-center border-2 border-dashed border-gray-300"
                          style={{
                            width: '32px',
                            height: '32px',
                            background: '#F9FAFB'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenShareModal(group);
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3.333v9.334M3.333 8h9.334" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Member Avatars - Fixed Height 40px */}
                  <div
                    className="flex justify-center w-full"
                    style={{
                      height: '56px',
                      borderTop: '1px solid #E4E7EB',
                      paddingTop: '20px',
                      paddingBottom: '0.5rem',
                      marginTop: 'auto',
                      flexShrink: 0
                    }}
                  >
                    <div className="flex" style={{ height: '28px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenShareModal(group);
                        }}
                        disabled={isGroupArchived(group)}
                        // onClick={() => handleOpenManageMembers(group)}
                        className="hover:bg-gray-50 transition-colors"
                        style={{
                          width: '145px',
                          height: '28px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px 0 0 8px',
                          fontFamily: 'Poppins',
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: '21px',
                          color: '#374151',
                          marginRight: '-1px'
                        }}
                      >
                        Share Access
                      </button>
                      <button
                        onClick={() => isGroupArchived(group) ? handleUnarchive(group.id) : handleArchive(group.id)}
                        className="hover:bg-gray-50 transition-colors"
                        style={{
                          width: '75px',
                          height: '28px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '0 8px 8px 0',
                          fontFamily: 'Poppins',
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: '21px',
                          color: isGroupArchived(group) ? '#2EA901' : '#374151'
                        }}
                      >
                        {isGroupArchived(group) ? 'Restore' : 'Archive'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        })}
      </>
      }

      {profileModalUser != null && (<ProfileDetailView user={profileModalUser}
        currentUser={currentUser}
        userUuid={profileModalUser.uuid}
        onClose={() => {
          setprofileModalUser(null);
        }}
      />)}

      {shareGroup != null && (
        <div className="fixed inset-0 flex items-center justify-center bg-customGray z-50 px-2 pb-2 sm:px-4 sm:pb-4 pt-20"
          // style={{ backgroundColor: '#6b7a8a' }}
          onClick={() => setShareGroup(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full h-full sm:h-auto max-w-4xl mx-auto sm:max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Share Group</h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">{shareGroup.title}</p>
              </div>
              <button
                onClick={() => setShareGroup(null)}
                className="text-gray-400 hover:text-gray-600 p-1 ml-2 flex-shrink-0"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 auto-rows-max">
                {/* Left Side - Add Users */}
                <div className="border-b lg:border-b-0 lg:border-r border-gray-200 pb-4 lg:pb-0 lg:pr-6">
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Share with Users</h3>

                  {/* Search */}
                  <div className="mb-3 sm:mb-4">
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={shareSearchTerm}
                      onChange={(e) => {
                        setShareSearchTerm(e.target.value)
                      }}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Available Users */}
                  <div className="overflow-y-auto space-y-2 border rounded-lg p-3 sm:p-4" style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '200px' }}>
                    {availableShareUsers
                      .filter((user) =>
                        `${user.firstName} ${user.lastName} ${user.role || ''}`
                          .toLowerCase()
                          .includes(shareSearchTerm.toLowerCase())
                      )
                      .map((user) => (
                        <div
                          key={user.uuid}
                          onClick={() => toggleShareUserSelection(user.uuid)}
                          className={`flex items-center justify-between p-2 sm:p-3 border rounded-lg cursor-pointer transition-colors duration-200 ${selectedShareUsers.includes(user.uuid)
                            ? 'bg-blue-50 border-blue-300'
                            : 'border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                            <img
                              src={user.croppedAvatarUrl}
                              onError={(e) => {
                                e.currentTarget.src = defaultAvatar;
                              }}
                              alt={user.firstName}
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex-shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {user.firstName} {user.lastName}
                              </p>
                              {user.role && (
                                <p className="text-xs text-gray-500 truncate">{user.role}</p>
                              )}
                            </div>
                          </div>
                          {selectedShareUsers.includes(user.uuid) && (
                            <span className="text-blue-500 font-bold flex-shrink-0 ml-2">✓</span>
                          )}
                        </div>
                      ))}
                    {availableShareUsers.filter((user) =>
                      `${user.firstName} ${user.lastName} ${user.role || ''}`
                        .toLowerCase()
                        .includes(shareSearchTerm.toLowerCase())
                    ).length === 0 && (
                        <div className="text-center py-6 sm:py-8 text-gray-500">
                          <p className="text-sm">No available users found</p>
                        </div>
                      )}
                  </div>

                  {/* Add Button */}
                  {selectedShareUsers.length > 0 && (
                    <div className="mt-3 sm:mt-4">
                      <button
                        onClick={handleShareUsers}
                        className="w-full px-4 py-2 sm:py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Share with {selectedShareUsers.length} User{selectedShareUsers.length !== 1 ? 's' : ''}
                      </button>
                    </div>
                  )}
                </div>

                {/* Right Side - Currently Shared Users */}
                <div>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4 flex-shrink-0">
                    Currently Shared With ({currentlySharedWith.length})
                  </h3>

                  <div className="overflow-y-auto space-y-2 border rounded-lg p-3 sm:p-4" style={{ maxHeight: '300px', minHeight: '200px' }}>
                    {currentlySharedWith.length === 0 ? (
                      <div className="text-center py-6 sm:py-8 text-gray-500">
                        <svg className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-300 mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <p className="text-xs sm:text-sm">Group not shared with anyone yet</p>
                        <p className="text-xs text-gray-400 mt-1 px-4">Select users from the left to share this group</p>
                      </div>
                    ) : (
                      currentlySharedWith.map((user) => (
                        <div
                          key={user.uuid}
                          className="flex items-center justify-between p-2 sm:p-3 border border-gray-200 rounded-lg bg-green-50"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                            <img
                              src={user.croppedAvatarUrl}
                              onError={(e) => {
                                e.currentTarget.src = defaultAvatar;
                              }}
                              alt={user.firstName}
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex-shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {user.firstName} {user.lastName}
                              </p>
                              {user.role && (
                                <p className="text-xs text-gray-500 truncate">{user.role}</p>
                              )}
                              <p className="text-xs text-green-600">Has access</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleUnshareUser(user.uuid)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded flex-shrink-0 ml-2"
                            title="Remove access"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-col sm:flex-row items-center justify-between p-4 sm:p-6 border-t border-gray-200 bg-gray-50 gap-3 sm:gap-0 flex-shrink-0">
              <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                {currentlySharedWith.length > 0 && (
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                    </svg>
                    <span>Shared users will receive email and in-app notifications</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShareGroup(null)}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-customGray flex items-center justify-center z-50 px-2 pb-2 sm:px-4 sm:pb-4 pt-20"
          // style={{ backgroundColor: '#6b7a8a' }}
          onClick={() => {
            setIsModalOpen(false);
          }}
        >
          <div
            className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md mx-4"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Create New Collection
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <input
                type="text"
                name="title"
                placeholder="Title"
                value={form.title}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                required
              />

              <div className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 flex flex-wrap gap-2 items-center min-h-12">
                {form.hashtag && form.hashtag.split('  ').filter(t => t.trim() !== '').map((tag, index) => (
                  <div key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 border border-blue-200 rounded-full text-sm text-blue-800">
                    <span>{tag}</span>
                    <button
                      type="button"
                      onClick={() => removeHashtag(tag)}
                      className="ml-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
                <input
                  type="text"
                  name="hashtag"
                  placeholder={form.hashtag ? "" : "#tags"}
                  value={currentHashtagInput}
                  onChange={(e) => setCurrentHashtagInput(e.target.value)}
                  onKeyDown={handleHashtagKeyDown}
                  className="flex-grow outline-none bg-transparent min-w-24"
                />
              </div>

              <textarea
                name="description"
                placeholder="Description"
                value={form.description}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-purple-500 rounded-lg text-base focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none resize-none"
                rows={5}
              />

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-3 rounded-lg text-base font-semibold bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 rounded-lg text-base font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  Create Collection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Members Modal - Responsive Design */}
      {manageMembersGroup && (
        <div
          className="fixed inset-0 bg-customGray flex items-center justify-center z-50 px-2 pb-2 sm:px-4 sm:pb-4 pt-20"
          // style={{ backgroundColor: '#6b7a8a' }}
          onClick={() => {
            setManageMembersGroup(null);
            setManageMembersActiveTab('add');
            setSelectedMembers([]);
          }}
        >
          {/* Mobile View (below 768px) - Tabbed Interface */}
          <div className="md:hidden w-full h-full flex items-center justify-center">
            <div
              className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto flex flex-col overflow-hidden"
              style={{ maxHeight: '90vh', height: '90vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Mobile Header */}
              <div className="flex items-start justify-between p-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex-1 pr-3">
                  <h2 className="text-base font-semibold text-gray-900 leading-tight">
                    Manage Members for {manageMembersGroup.title} collection
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setManageMembersGroup(null);
                    setManageMembersActiveTab('add');
                    setSelectedMembers([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Mobile Tabs */}
              {manageMembersGroup.isOwner && (
                <div className="flex border-b border-gray-200 flex-shrink-0">
                  <button
                    onClick={() => setManageMembersActiveTab('add')}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${manageMembersActiveTab === 'add'
                      ? 'text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Invite as Viewers
                    {manageMembersActiveTab === 'add' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                    )}
                  </button>
                  <button
                    onClick={() => setManageMembersActiveTab('current')}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${manageMembersActiveTab === 'current'
                      ? 'text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Current Access
                    {manageMembersActiveTab === 'current' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
                    )}
                  </button>
                </div>
              )}

              {/* Mobile Tab Content */}
              <div className="flex-1 overflow-hidden flex flex-col">
                {manageMembersActiveTab === 'add' && manageMembersGroup.isOwner ? (
                  // Mobile - Invite as Viewers Tab
                  <div className="flex-1 overflow-hidden flex flex-col">
                    {/* Scrollable Content Area */}
                    <div className="flex-1 overflow-y-auto">
                      <div className="p-4">
                        <h3 className="text-base font-semibold text-gray-900 mb-3">
                          Invite as Viewers
                        </h3>

                        {/* Search */}
                        <div className="mb-3">
                          <input
                            type="text"
                            placeholder="Search Filmik Users"
                            value={searchMember}
                            onChange={(e) => setSearchMember(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                          />
                        </div>

                        {/* Available Members List */}
                        <div className="space-y-2">
                          {availableUsersForManage
                            .filter((user) =>
                              `${user.firstName} ${user.lastName}`
                                .toLowerCase()
                                .includes(searchMember.toLowerCase())
                            )
                            .map((user) => {
                              const isSelected = selectedMembers.includes(user.uuid);

                              return (
                                <div
                                  key={user.uuid}
                                  onClick={() => toggleMemberSelection(user.uuid)}
                                  className={`flex items-center justify-between p-3 border rounded-xl cursor-pointer transition-all ${isSelected
                                    ? 'bg-blue-50 border-blue-300'
                                    : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <img
                                      src={user.croppedAvatarUrl || defaultAvatar}
                                      alt={user.firstName}
                                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {user.firstName} {user.lastName}
                                      </p>
                                    </div>
                                  </div>

                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ml-2 ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                    }`}>
                                    {isSelected && (
                                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          {availableUsersForManage.filter((user) =>
                            `${user.firstName} ${user.lastName}`
                              .toLowerCase()
                              .includes(searchMember.toLowerCase())
                          ).length === 0 && (
                              <div className="text-center py-6 text-gray-500">
                                <p className="text-sm">No available members found</p>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>

                    {/* Fixed Add Users Button - Always Visible */}
                    {selectedMembers.length > 0 && (
                      <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
                        <button
                          onClick={handleAddMembersToGroup}
                          className="w-full px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add {selectedMembers.length} User{selectedMembers.length !== 1 ? 's' : ''}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  // Mobile - Current Access Tab
                  <div className="flex-1 overflow-hidden flex flex-col">
                    {/* Scrollable Content Area */}
                    <div className="flex-1 overflow-y-auto">
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-base font-semibold text-gray-900">
                            Current Access
                          </h3>
                          <span className="text-sm text-gray-500">
                            {currentMembers.length} viewer{currentMembers.length !== 1 ? 's' : ''}
                          </span>
                        </div>

                        {/* Current Members List */}
                        <div className="space-y-2">
                          {currentMembers.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              <p className="text-sm">No members in this collection yet</p>
                            </div>
                          ) : (
                            currentMembers.map((user) => {
                              const isOwner = user.uuid === currentUser.uuid;

                              return (
                                <div
                                  key={user.uuid}
                                  className="flex items-center justify-between p-3 border border-gray-200 rounded-xl bg-white"
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <img
                                      src={user.croppedAvatarUrl || defaultAvatar}
                                      alt={user.firstName}
                                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {user.firstName} {user.lastName}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {isOwner ? 'Owner' : 'Viewer'}
                                      </p>
                                    </div>
                                  </div>
                                  {manageMembersGroup?.isOwner && !isOwner && (
                                    <button
                                      onClick={() => handleRemoveMemberFromGroup(user.uuid)}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full flex-shrink-0 ml-2"
                                      title="Remove access"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Footer - Active Status and Archive */}
                    {manageMembersGroup.isOwner && (
                      <div className="border-t border-gray-200 bg-gray-50 p-4 flex-shrink-0 space-y-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <div className="flex items-center justify-center w-5 h-5 bg-green-100 rounded-full flex-shrink-0">
                            <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span className="font-medium text-green-700">Active</span>
                          <span className="text-xs">Viewers will receive email and in-app notifications.</span>
                        </div>

                        <button
                          onClick={() => {
                            if (manageMembersGroup) {
                              handleArchive(manageMembersGroup.id);
                            }
                          }}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                          </svg>
                          Archive Collection
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Desktop View (768px and above) - Two Column Layout - UNCHANGED */}
          <div
            className="hidden md:flex bg-white rounded-xl shadow-xl w-full max-w-4xl mx-auto overflow-hidden flex-col"
            style={{ maxHeight: '85vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Desktop Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {manageMembersGroup.isOwner ? 'Manage Members for' : 'View Members of'} {manageMembersGroup.title} collection
                </h2>
              </div>
              <button
                onClick={() => setManageMembersGroup(null)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Desktop Two Column Layout */}
            <div className="flex flex-1 overflow-hidden" style={{ height: 'calc(85vh - 80px)' }}>
              {/* Left Column - Add Members (only if owner) */}
              {manageMembersGroup?.isOwner && (
                <div className="w-1/2 flex flex-col border-r border-gray-200 bg-white overflow-hidden">
                  {/* Header - Fixed */}
                  <div className="p-6 border-b border-gray-200 flex-shrink-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      Add Members
                    </h3>
                  </div>

                  {/* Search - Fixed */}
                  <div className="px-6 pt-4 pb-2 flex-shrink-0">
                    <input
                      type="text"
                      placeholder="Search members..."
                      value={searchMember}
                      onChange={(e) => setSearchMember(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  {/* Available Members List - Scrollable */}
                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    <div className="space-y-2">
                      {availableUsersForManage
                        .filter((user) =>
                          `${user.firstName} ${user.lastName}`
                            .toLowerCase()
                            .includes(searchMember.toLowerCase())
                        )
                        .map((user) => {
                          const isSelected = selectedMembers.includes(user.uuid);

                          return (
                            <div
                              key={user.uuid}
                              onClick={() => toggleMemberSelection(user.uuid)}
                              className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all ${isSelected
                                ? 'bg-blue-50 border-blue-300'
                                : 'border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <img
                                  src={user.croppedAvatarUrl}
                                  onError={(e) => {
                                    e.currentTarget.src = defaultAvatar;
                                  }}
                                  alt={user.firstName}
                                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {user.firstName} {user.lastName}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                  }`}>
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      {availableUsersForManage.filter((user) =>
                        `${user.firstName} ${user.lastName}`
                          .toLowerCase()
                          .includes(searchMember.toLowerCase())
                      ).length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <p className="text-sm">No available members found</p>
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Add Button - Fixed at Bottom */}
                  {selectedMembers.length > 0 && (
                    <div className="p-6 border-t border-gray-200 bg-white flex-shrink-0">
                      <button
                        onClick={handleAddMembersToGroup}
                        className="w-full px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add {selectedMembers.length} User{selectedMembers.length !== 1 ? 's' : ''}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Right Column - Current Members */}
              <div className={`flex flex-col bg-white overflow-hidden ${!manageMembersGroup?.isOwner ? 'w-full' : 'w-1/2'}`}>
                {/* Header - Fixed */}
                <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Current Members
                    <span className="ml-2 text-base font-normal text-gray-500">
                      ({currentMembers.length})
                    </span>
                  </h3>
                </div>

                {/* Current Members List - Scrollable */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {currentMembers.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p className="text-sm">No members in this collection yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {currentMembers
                        .filter((user) =>
                          `${user.firstName} ${user.lastName}`
                            .toLowerCase()
                            .includes(searchMember.toLowerCase())
                        )
                        .map((user) => {
                          const isOwner = user.uuid === currentUser.uuid;

                          return (
                            <div
                              key={user.uuid}
                              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <img
                                  src={user.croppedAvatarUrl}
                                  onError={(e) => {
                                    e.currentTarget.src = defaultAvatar;
                                  }}
                                  alt={user.firstName}
                                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {user.firstName} {user.lastName}
                                  </p>
                                  {isOwner && (
                                    <p className="text-xs text-gray-500">Owner</p>
                                  )}
                                </div>
                              </div>
                              {manageMembersGroup?.isOwner && !isOwner && (
                                <button
                                  onClick={() => handleRemoveMemberFromGroup(user.uuid)}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0 ml-2"
                                  title="Remove member"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>

                {/* Desktop Footer - Fixed */}
                {manageMembersGroup.shared && (
                  <div className="border-t border-gray-200 bg-gray-50 p-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="flex items-center justify-center w-5 h-5 bg-green-100 rounded-full">
                        <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="font-medium text-green-700">Active</span>
                      <span className="ml-2">Viewers will receive email and in-app notifications.</span>
                    </div>
                    {manageMembersGroup.isOwner && (
                      <button
                        onClick={() => handleArchive(manageMembersGroup.id)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Archive Collection
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Add Favorites Modal */}
      {showAddFavoritesModal && (
        addFavRender()
      )}
      {/* Notification Component */}
      {notification && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 w-11/12 sm:w-auto animate-slide-down" style={{ zIndex: 8888 }}>
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border ${notification.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : notification.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}
            style={{ minWidth: '300px', maxWidth: '90vw' }}
          >
            <div className="flex-shrink-0">
              {notification.type === 'success' && (
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {notification.type === 'error' && (
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {notification.type === 'info' && (
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" strokeWidth={2} />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-4m0-4h.01" />
                </svg>
              )}
            </div>
            <p className="text-sm font-medium flex-1">{notification.message}</p>
            <button
              onClick={() => setNotification(null)}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div >
  );
}